import {
  DXF_POLYLINE_FLAG,
  coerceColor,
  colorToLegacyString,
  computeBlockUnitFactor,
  polylineIsClosed,
  polylineSubtype,
  resolveTableStyleName,
  tableRotationDegrees,
  exportDrawingDocumentToDxfString,
  importDxfStringToDrawingDocument,
  importDxfStringToDrawingDocumentWithProgress,
  readDynVarsFromDrawingDocument,
  serializeColor,
  type EntityColor,
  type ImportProgressCallback,
} from "@dxf-ai-editor/drawing-model";
import {
  deriveEllipsePreviewProperties,
  effectiveMTextAngleDeg,
  heavyPolylinePathMetrics,
  isReflectedOcs,
  patternTypeLabel,
} from "@dxf-ai-editor/command-engine";
import type { SysvarValues } from "../dynamicInput/sysvarSchema";
import type {
  ArcEntity,
  ArrayRectEntity,
  BlockInsertEntity,
  BlockRecord,
  CircleEntity,
  DimensionEntity,
  DimensionStyle,
  DrawingDocument,
  DrawingEntity,
  EllipseEntity,
  HatchEntity,
  LineEntity,
  LwpolylineEntity,
  MleaderLeaderRoot,
  MtextEntity,
  MultileaderEntity,
  MultileaderStyle,
  PointEntity,
  OleFrameEntity,
  PolylineEntity,
  RevisionCloudEntity,
  SplineEntity,
  TableEntity,
  WipeoutEntity,
  TextEntity,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import type { DrawingSpace, EntityPreview } from "@dxf-ai-editor/contracts";
import { t, type MessageKey } from "../i18n";
import { rgbToHex } from "../color/rgbCodec";
import { resolveGradientRamp } from "../scene3d/hatch/gradientRamp";
import { SCENE_STOP_RESOLVERS } from "../scene3d/hatch/sceneGradientResolvers";
import { getEntityProvenance } from "./mutationStore";
import { shadePlotModeEnglishLabel } from "./viewportShadePlot";

function commonProperties(entity: {
  layer?: string;
  // EPIC-COLOR-UNION S1 — accepts the typed `EntityColor` union directly off the
  // entity (callers pass the whole `DrawingEntity`); projected to the legacy
  // `Color` string here so the property bag stays byte-identical for ByLayer /
  // index, and a true-colour entity surfaces "#rrggbb".
  color?: EntityColor;
  linetype?: string;
  linetypeScale?: number;
  lineweight?: string;
  thickness?: number;
  transparency?: string | number;
  arrayGroupId?: string;
  arrayMode?: string;
}) {
  const base: Record<string, string> = {
    Layer: entity.layer ?? "0",
    // `coerceColor` defends against raw literals / legacy strings that bypassed
    // the schema (absent → byLayer; string → union), then project to legacy.
    Color: colorToLegacyString(coerceColor(entity.color)),
    Linetype: entity.linetype ?? "ByLayer",
    Lineweight: entity.lineweight ?? "ByLayer",
    Thickness: String(entity.thickness ?? 0),
    Transparency: String(entity.transparency ?? 0),
  };
  // PR-LT3 variant A (A2) — FIX-3 (Codex P2). Project the imported per-entity
  // linetype scale (DXF group 48 → drawing-model `linetypeScale`) into the
  // common property bag under the Title-Case key the scene adapter reads
  // (`adapterContext.parseLinetypeScale` → `RenderableEntity.linetypeScale`).
  // Without this row the A2 projection defaulted every imported entity to 1,
  // ignoring the authored CELTSCALE. Emitted ONLY when the field is present
  // (mirrors the conditional `arrayGroupId` / `arrayMode` rows), so the
  // thousands of entities with no group 48 stay byte-identical.
  if (entity.linetypeScale !== undefined) {
    base["Linetype Scale"] = String(entity.linetypeScale);
  }
  if (entity.arrayGroupId) {
    base["Array group"] = entity.arrayGroupId;
  }
  if (entity.arrayMode) {
    base["Array mode"] = entity.arrayMode;
  }
  return base;
}

// defaultPreviewProperties removed — use commonProperties(entity) instead

const lineEditableProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
  "Start X",
  "Start Y",
  // #317 residual R2 — imported DXF LINEs expose an editable Z (group 30/31) in
  // the Properties palette, matching the command-engine preview list
  // (simpleGeometryLinePreviewProperties) + AutoCAD parity (Point "Position Z" /
  // Circle+Arc "Center Z"). Without these rows the common imported-line path
  // could not show or edit a 3-D line's elevation, and any X/Y edit reached
  // readLineState with a missing Z (defaulted to 0 → flattened).
  "Start Z",
  "End X",
  "End Y",
  "End Z",
  "Length",
  "Angle",
];

const pointEditableProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
  "Position X",
  "Position Y",
  "Position Z",
  "Rotation",
];

const circleEditableProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
  "Center X",
  "Center Y",
  "Center Z",
  "Radius",
  "Diameter",
  "Circumference",
  "Area",
];

const arcEditableProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
  "Center X",
  "Center Y",
  "Center Z",
  "Radius",
  "Start angle",
  "End angle",
  "Total angle",
  "Arc length",
];

// ELLIPSE editable set — the SAME members as the command-engine's
// `ellipsePreviewProperties` (packages/command-engine/src/previewBuilders.ts):
// AutoCAD's ellipse Properties panel exposes center + major-axis endpoint
// vector + minor/major ratio + start/end parameters, and the grip/render
// consumers (gripCalculation "Ellipse" arm, scene3d ellipseToRenderable)
// read exactly these keys. Kept aligned so a command-drawn and an imported
// ellipse expose an identical editable surface.
const ellipseEditableProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
  "Center X",
  "Center Y",
  "Major axis X",
  "Major axis Y",
  "Ratio",
  "Start parameter",
  "End parameter",
];

export type DxfImportResult =
  | {
      ok: true;
      message: string;
      entityCount: number;
      spaceCount: number;
    }
  | {
      ok: false;
      message: string;
    };

/**
 * Project the LIVE editable HEADER sysvars (MIRRTEXT, OSMODE) from the session
 * sysvar store onto a copy of the export document (Codex #342 F1).
 *
 * SETVAR updates ONLY the live sysvar store (`useSysvarStore`), while DXF export
 * reads `document.mirrtext` / `document.osmode` (via the bridge's
 * `collectDocumentVars` / `collectSnapVars`). Without this projection a
 * `SETVAR MIRRTEXT 1 → save → reload` would drop back to the stale document
 * value. MIRRTEXT and OSMODE are the ONLY HEADER vars with a live SETVAR slot
 * (`DYN_DEFAULTS`), so they are the only two that can diverge; INSUNITS / PDMODE
 * have no sysvar store (no SETVAR path) and stay document-sourced — the fix is
 * therefore UNIFORM across every header var that can actually diverge.
 *
 * Single source of truth for BOTH export paths — the store `exportDxfText`
 * action AND the App.tsx download/copy/Ctrl+S path call this, so neither one
 * silently drops the live value (no per-site clone). On a plain import with no
 * SETVAR the live store was hydrated FROM the document, so the projected values
 * equal the document fields and this is a no-op (import→export round-trip
 * preserved).
 */
export function applyLiveHeaderSysvars(
  document: DrawingDocument,
  liveSysvars: { MIRRTEXT: number; OSMODE: number },
): DrawingDocument {
  if (
    document.mirrtext === liveSysvars.MIRRTEXT &&
    document.osmode === liveSysvars.OSMODE
  ) {
    return document;
  }
  return {
    ...document,
    mirrtext: liveSysvars.MIRRTEXT,
    osmode: liveSysvars.OSMODE,
  };
}

/**
 * Build the sysvar-hydration map applied to the live store after a DXF OPEN
 * (Codex #342 F1 / NEW-1). This is the INVERSE direction of
 * {@link applyLiveHeaderSysvars}: import reads the document's header vars INTO
 * the live store so a subsequent SETVAR / save sees the loaded file's values.
 *
 * It MUST cover EVERY header var the export projection writes back from the live
 * store — currently MIRRTEXT and OSMODE. If a var were exported-from-live but
 * NOT hydrated-from-document, the live store would keep its default after open
 * and the FIRST save (with no SETVAR ever issued) would project that stale
 * default back over the file's value, silently corrupting it on a plain
 * open→save round-trip. Hydrating both keeps the projection a genuine no-op
 * (live == document) until the user actually issues a SETVAR.
 *
 * Single source of truth: App.tsx's OPEN path calls THIS, so the import→export
 * seam (hydrate-in / project-out) is kept symmetric in one module — no per-site
 * drift between the two header-sysvar directions.
 */
export function buildHeaderSysvarHydration(
  document: DrawingDocument,
): Partial<SysvarValues> {
  const vars: Partial<SysvarValues> = {
    // DYN* HEADER family ($DYNMODE / $DYNDIGRIP / …), round-tripped via HEADER.
    ...(readDynVarsFromDrawingDocument(document) as Partial<SysvarValues>),
  };
  // MIRRTEXT + OSMODE are the two HEADER vars with a live-SETVAR slot that the
  // export projection writes back from the live store; hydrate BOTH so the
  // projection is a no-op until a real SETVAR (NEW-1: OSMODE was missing here,
  // corrupting $OSMODE on the first save of any file with a non-default value).
  if (typeof document.mirrtext === "number") {
    vars.MIRRTEXT = document.mirrtext;
  }
  if (typeof document.osmode === "number") {
    vars.OSMODE = document.osmode;
  }
  return vars;
}

// ── CECOLOR ($CECOLOR) hydrate-in / project-out seam ─────────────────────────
//
// CECOLOR is the typed `EntityColor` UNION, so it CANNOT ride the numeric
// `applyLiveHeaderSysvars` / `buildHeaderSysvarHydration` pair above (those are
// `{ MIRRTEXT: number; OSMODE: number }`). It gets its OWN sibling helpers here,
// in the SAME module, so the hydrate-in / project-out directions stay symmetric
// in one place — the live current-colour value lives in `cecolorStore` (a union
// cannot ride the integer-only sysvar schema). `doc.cecolor` round-trips the
// file; the store is the live session value; this seam keeps them aligned.

/**
 * Project the LIVE current colour (from `cecolorStore`) onto the export document
 * so a `COLOR`-then-save persists the live value, not the stale document field.
 * Direction: live store → `doc.cecolor`. No-op (returns the SAME document) when
 * the live colour already equals `doc.cecolor` — so a plain open→save round-trip
 * (where the store was hydrated FROM the document) is byte-identical (compared
 * via the lossless `serializeColor` codec so true-colour / colour-book current
 * colours compare correctly).
 *
 * Single source of truth for BOTH export paths — the store `exportDxfText`
 * action AND App.tsx's download/copy/Ctrl+S path call this, mirroring
 * `applyLiveHeaderSysvars`.
 */
export function applyLiveCeColor(
  document: DrawingDocument,
  liveCeColor: EntityColor,
): DrawingDocument {
  if (serializeColor(document.cecolor) === serializeColor(liveCeColor)) {
    return document;
  }
  return { ...document, cecolor: liveCeColor };
}

/**
 * Build the CECOLOR value to push into `cecolorStore` after a DXF OPEN — the
 * INVERSE direction of {@link applyLiveCeColor}: import reads the document's
 * `$CECOLOR` INTO the live store so a subsequent COLOR / save sees the loaded
 * file's value (and so the project-out is a genuine no-op until the user issues
 * a COLOR). Mirrors `buildHeaderSysvarHydration` for the union case.
 *
 * It MUST be paired with the INSERT-preserve merge rule (`storeHelpers`
 * `mergeCeColor`): on OPEN of an empty seed the merged doc carries the imported
 * cecolor → hydration takes it; on INSERT into a live drawing the merge keeps
 * the live cecolor → hydrating the merged value is a no-op (the live colour is
 * not wiped). Single source of truth: the OPEN path calls THIS.
 */
export function buildCecolorHydration(document: DrawingDocument): EntityColor {
  return document.cecolor ?? { kind: "byLayer" };
}

export function exportGeometryDocumentToDxfText(
  document: DrawingDocument,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): string {
  // PR1b Codex P2 — forward the per-space cache snapshot so a STREAMED document
  // (empty inline `space.entities[]`) serialises its cache-backed entities.
  // Mirrors App.tsx `buildExportedDxfText`; absent snapshot falls through to
  // the legacy inline-array path (backward-compatible).
  //
  // DXF-CONFORMANT-SKELETON — this is a GENUINE user-facing SAVE seam, so it
  // opts into conformant-skeleton back-fill: a File → New drawing (no imported
  // raw source) gets the HEADER / CLASSES / VPORT / APPID / BLOCK_RECORD /
  // BLOCKS / OBJECTS root-dict structure + hex entity handles so the saved DXF
  // opens in AutoCAD / passes a strict ezdxf readfile. Imported documents are
  // unaffected (carry-forward returns them unchanged).
  return exportDrawingDocumentToDxfString(document, {
    ensureConformant: true,
    ...(entitiesSnapshot !== undefined ? { entitiesSnapshot } : {}),
  });
}

export function importGeometryDocumentFromDxfText(
  source: string,
  options: {
    documentId: string;
    title: string;
  },
): DrawingDocument {
  return importDxfStringToDrawingDocument(source, options);
}

/**
 * Async, progress-aware variant of
 * {@link importGeometryDocumentFromDxfText}. Calls `onProgress` at phase
 * boundaries so the UI progress dialog can redraw between the heavy
 * tokenize, entities and bridge phases of the DXF parser.
 *
 * The sync variant remains available for callers that don't need
 * progress (tests, round-trip helpers in command-engine, etc.).
 */
export async function importGeometryDocumentFromDxfTextWithProgress(
  source: string,
  options: {
    documentId: string;
    title: string;
    onProgress?: ImportProgressCallback;
  },
): Promise<DrawingDocument> {
  return importDxfStringToDrawingDocumentWithProgress(source, options);
}

export function buildShellSpacesFromGeometryDocument(
  document: DrawingDocument,
): DrawingSpace[] {
  return document.spaces.map((space) => {
    // Contract's spaceKindSchema only encodes "model" | "layout" — the
    // synthetic block-edit / block-test kinds (Phase 2.3 / DYN-1.9) are
    // projected onto the model kind for shell purposes, with a
    // distinctive pageSetup label that the UI can pick up. The block
    // Editor tint / status bar indicator reads from
    // `geometryDocument.spaces[active].kind` directly instead of
    // routing through the shell space kind.
    const shellKind = space.kind === "layout" ? "layout" : "model";
    const pageSetup =
      space.kind === "block-edit"
        ? `Block Editor: ${space.blockEditName ?? ""}`
        : space.kind === "block-test"
          ? "Test block sandbox"
          : space.kind === "model"
            ? "Imported DXF model space"
            : "Imported DXF layout space";
    return {
      id: space.id,
      kind: shellKind as const,
      name: space.name,
      pageSetup,
      viewports: [],
    };
  });
}

/**
 * PR-V3 — Defensive ceiling on shell-entity preview construction.
 *
 * Previous cap was 30k (SVG-era main-thread budget). PR-V2 migrated
 * Scene3DStage to a cache-direct render path that reads from the
 * non-reactive `__entitiesCache` + `entityMetadataIndex` for huge
 * drawings, so the cap no longer needs to gate visible geometry.
 *
 * The 5_000_000 ceiling is a defensive limit that should never
 * trigger in practice (the largest known fixture — П35Р — has 685k
 * entities, and even pathological synthetic inputs stay well below
 * 5M). It exists purely to bound the preview-building loop in case
 * a malformed DXF / DoS-grade input slips past upstream validation.
 *
 * Per CLAUDE.md "ничего не упрощать": all entities reachable via
 * cache + metadata index. Truncation is reserved for the defensive
 * ceiling only.
 */
const SHELL_ENTITY_CAP = 5_000_000;

/**
 * PR-V3 — threshold above which the import path skips the
 * preview-building pass and writes an empty `state.drawing.entities`
 * array. Scene3DStage reads from `__entitiesCache` + metadata index
 * directly (cache-direct), and the few UI consumers that iterate
 * `state.drawing.entities` (entity list, properties palette) fall
 * back to metadata aggregates.
 *
 * Set to 5_000 so АБИ1.dxf-class fixtures keep the legacy reactive
 * path (full previews available to small-file panels) and only large
 * imports — where the per-entity preview build would dominate
 * import time and consume hundreds of MB of transient memory —
 * switch to the cache-direct path.
 *
 * Exported for the editorStore import flow; consumers should not
 * need to reach this value directly.
 */
export const SHELL_ENTITY_PREVIEW_THRESHOLD = 5_000;

/**
 * Optional lookup that returns the non-reactive entity list for a
 * given space when imports above the cache-stash threshold (commit
 * 7202ba4) emptied `space.entities` in the reactive document. Wired
 * by editorStore via `getCachedEntitiesForSpace`; passed in to keep
 * this module pure and testable.
 */
export type CachedEntitiesLookup = (
  spaceId: string,
) => readonly DrawingEntity[] | undefined;

/**
 * Count entities across every space in `document`, falling back to
 * the cache lookup when the reactive document's `space.entities` is
 * empty (huge-import stash path). Used by the import flow to decide
 * whether to run the preview-building pass at all (PR-V3).
 */
export function countEntitiesAcrossSpaces(
  document: DrawingDocument,
  cacheLookup?: CachedEntitiesLookup,
): number {
  let total = 0;
  for (const space of document.spaces) {
    total +=
      space.entities.length > 0
        ? space.entities.length
        : (cacheLookup?.(space.id)?.length ?? 0);
  }
  return total;
}

export function buildShellEntityPreviewsFromGeometryDocument(
  document: DrawingDocument,
  cacheLookup?: CachedEntitiesLookup,
): EntityPreview[] {
  const dimStylesByName = new Map<string, DimensionStyle>();
  for (const s of document.styleTables?.dimensionStyles ?? []) {
    dimStylesByName.set(s.name, s);
  }
  const blockRecords = document.blockRecords ?? {};
  const blocks = document.blocks ?? {};
  const drawingInsunits = document.insunits ?? 0;
  // local-Codex-review P2 — this document is not necessarily the currently
  // ACTIVE one (a caller could resync a background/inactive document);
  // forward its own id into every preview so `getEntityProvenance` reads
  // THAT document's ledger, not whichever document happens to be active
  // at call time. See `resolveEntityProvenanceSummary`'s doc comment.
  const documentId = document.id;
  const out: EntityPreview[] = [];
  outer: for (const space of document.spaces) {
    // Fall back to the non-reactive cache when the reactive document's
    // space.entities was emptied by the stash path (huge imports). Three.
    // js renderer handles 685k+ entities natively, so we no longer need
    // the SVG-era cap of 30k.
    const source =
      space.entities.length > 0
        ? space.entities
        : cacheLookup?.(space.id) ?? [];
    for (const entity of source) {
      out.push(
        buildShellEntityPreview(
          entity,
          dimStylesByName,
          blockRecords,
          blocks,
          drawingInsunits,
          documentId,
          document.rawObjectGroups,
        ),
      );
      if (out.length >= SHELL_ENTITY_CAP) break outer;
    }
  }
  return out;
}

/**
 * Defect fix (command-created-line-provenance) — per-entity-type i18n key
 * pair for the Properties-panel provenance note. `imported` is the
 * pre-existing "audit.imported_*" family (baseline commit f541731e0,
 * written when every entity reaching this dispatcher genuinely came from
 * a DXF import — command-line entity creation did not exist yet).
 * `authored` is its command-created counterpart, added to BOTH i18n
 * catalogs alongside it (apps/web/src/app/i18n/messages.ts).
 *
 * `Record<DrawingEntity["type"], …>` — the compiler enforces this stays
 * exhaustive over every entity type `buildShellEntityPreview` handles
 * (same exhaustiveness the switch below relies on), so a future entity
 * type cannot silently fall through without a provenance note.
 */
const ENTITY_PROVENANCE_SUMMARY_KEYS: Record<
  DrawingEntity["type"],
  { imported: MessageKey; authored: MessageKey }
> = {
  Line: { imported: "audit.imported_line", authored: "audit.authored_line" },
  Point: {
    imported: "audit.imported_point",
    authored: "audit.authored_point",
  },
  Circle: {
    imported: "audit.imported_circle",
    authored: "audit.authored_circle",
  },
  Arc: { imported: "audit.imported_arc", authored: "audit.authored_arc" },
  Ellipse: {
    imported: "audit.imported_ellipse",
    authored: "audit.authored_ellipse",
  },
  Spline: {
    imported: "audit.imported_spline",
    authored: "audit.authored_spline",
  },
  LWPOLYLINE: {
    imported: "audit.imported_polyline",
    authored: "audit.authored_polyline",
  },
  Text: { imported: "audit.imported_text", authored: "audit.authored_text" },
  MText: {
    imported: "audit.imported_mtext",
    authored: "audit.authored_mtext",
  },
  Dimension: {
    imported: "audit.imported_dimension",
    authored: "audit.authored_dimension",
  },
  Multileader: {
    imported: "audit.imported_multileader",
    authored: "audit.authored_multileader",
  },
  "Revision Cloud": {
    imported: "audit.imported_revcloud",
    authored: "audit.authored_revcloud",
  },
  Wipeout: {
    imported: "audit.imported_wipeout",
    authored: "audit.authored_wipeout",
  },
  Hatch: {
    imported: "audit.imported_hatch",
    authored: "audit.authored_hatch",
  },
  Block: {
    imported: "audit.imported_block",
    authored: "audit.authored_block",
  },
  "Rectangular Associative Array": {
    imported: "audit.imported_array",
    authored: "audit.authored_array",
  },
  Viewport: {
    imported: "audit.imported_viewport",
    authored: "audit.authored_viewport",
  },
  Polyline: {
    imported: "audit.imported_heavy_polyline",
    authored: "audit.authored_heavy_polyline",
  },
  Table: {
    imported: "audit.imported_table",
    authored: "audit.authored_table",
  },
  "OLE Frame": {
    imported: "audit.imported_ole_frame",
    authored: "audit.authored_ole_frame",
  },
};

/**
 * Resolve the provenance-aware Properties-panel note for `entity`.
 * `getEntityProvenance` reads the ledger `commitMutation` populates at the
 * single CAD-mutation choke point (ПРИОРИТЕТ #2.1) — an id absent from it
 * never passed through a command/agent/collab mutation and is therefore
 * genuinely imported (see the ledger's doc comment in `mutationStore.ts`).
 *
 * `documentId` — local-Codex-review P2 — the ledger is scoped per document
 * id (multiple documents can be open at once; a dense per-type id like
 * "line-1" is not globally unique). Omit only when the caller has no
 * document context of its own (tests, and callers that only ever operate
 * on the currently-active document); `getEntityProvenance` falls back to
 * the active document in that case, same as every other legacy
 * `activeHolder()`-routed export in `mutationStore.ts`.
 */
function resolveEntityProvenanceSummary(
  entity: DrawingEntity,
  documentId?: string,
): string {
  const keys = ENTITY_PROVENANCE_SUMMARY_KEYS[entity.type];
  const provenance = getEntityProvenance(entity.id, documentId);
  return t(provenance === "authored" ? keys.authored : keys.imported);
}

/**
 * PR-V2 — exported so the cache-direct render path
 * (`Scene3DStage` viewport-cull) can build per-id previews on demand
 * without rebuilding the whole document. The signature is identical to
 * the original (private) helper used by
 * `buildShellEntityPreviewsFromGeometryDocument`.
 */
export function buildShellEntityPreview(
  entity: DrawingEntity,
  dimStylesByName: Map<string, DimensionStyle>,
  blockRecords: Record<string, BlockRecord>,
  blocks: Record<
    string,
    import("@dxf-ai-editor/drawing-model").BlockDefinition
  >,
  // Drawing-level $INSUNITS — forwarded to the Block arm so an inserted
  // block's "Block Unit Factor" reflects the real unit-conversion ratio.
  // Optional (default 0/Unitless → factor 1) so existing callers and the
  // parity/PDF-converter call sites compile unchanged.
  drawingInsunits = 0,
  // local-Codex-review P2 — the owning document's id, when the caller has
  // one in hand (`buildShellEntityPreviewsFromGeometryDocument` below
  // forwards `document.id`). See `resolveEntityProvenanceSummary`.
  documentId?: string,
  // The owning document's retained OBJECTS-section groups
  // (`DrawingDocument.rawObjectGroups`), forwarded so an ACAD_TABLE can resolve
  // its TABLESTYLE's real NAME (group 342 names the style by HANDLE; the name
  // lives on the TABLESTYLE object). Optional so every existing call site — the
  // PDF converter, the parity harness — compiles unchanged; without it the
  // "Table style" row reads empty rather than showing a fabricated default.
  rawObjectGroups?: readonly { code: number; value: string }[],
): EntityPreview {
  const preview = dispatchEntityPreviewByType(
    entity,
    dimStylesByName,
    blockRecords,
    blocks,
    drawingInsunits,
    rawObjectGroups,
  );
  // Defect fix (command-created-line-provenance) — every `build*Preview`
  // arm below hardcodes an "imported from DXF" summary (see the key-map
  // doc comment above); override it here, ONCE, with the entity's REAL
  // provenance instead of touching all seventeen builders individually.
  return {
    ...preview,
    summary: resolveEntityProvenanceSummary(entity, documentId),
  };
}

function dispatchEntityPreviewByType(
  entity: DrawingEntity,
  dimStylesByName: Map<string, DimensionStyle>,
  blockRecords: Record<string, BlockRecord>,
  blocks: Record<
    string,
    import("@dxf-ai-editor/drawing-model").BlockDefinition
  >,
  drawingInsunits: number,
  rawObjectGroups?: readonly { code: number; value: string }[],
): EntityPreview {
  switch (entity.type) {
    case "Line":
      return buildLinePreview(entity);
    case "Point":
      return buildPointPreview(entity);
    case "Circle":
      return buildCirclePreview(entity);
    case "Arc":
      return buildArcPreview(entity);
    case "Ellipse":
      return buildEllipsePreview(entity);
    case "Spline":
      return buildSplinePreview(entity);
    case "LWPOLYLINE":
      return buildLwpolylinePreview(entity);
    case "Text":
      return buildTextPreview(entity);
    case "MText":
      return buildMTextPreview(entity);
    case "Dimension":
      return buildDimensionPreview(entity, dimStylesByName);
    case "Multileader":
      return buildMultileaderPreview(entity);
    case "Revision Cloud":
      return buildRevisionCloudPreview(entity);
    case "Wipeout":
      return buildWipeoutPreview(entity);
    case "Hatch":
      return buildHatchPreview(entity);
    case "Block":
      return buildBlockInsertPreview(
        entity,
        blockRecords,
        blocks,
        drawingInsunits,
      );
    case "Rectangular Associative Array":
      return buildArrayRectPreview(entity);
    case "Viewport":
      return buildViewportPreview(entity);
    case "Polyline":
      return buildPolylinePreview(entity);
    case "Table":
      return buildTablePreview(entity, rawObjectGroups);
    case "OLE Frame":
      return buildOleFramePreview(entity);
    default: {
      // STATICALLY exhaustive over `DrawingEntity["type"]` — a NEW
      // `drawingEntitySchema` arm fails tsc HERE (assignment to `never`)
      // instead of surfacing as a runtime import-crash the way the missing
      // Ellipse arm did (every imported/resynced document funnels through
      // this switch). Mirrors `captureEntityGeometry`'s `toEntityGeometry`
      // default arm. The throw stays as the unreachable runtime fallback for
      // non-schema-validated inputs; it is dead code for the typed union.
      const exhaustive: never = entity;
      throw new Error(
        `Unsupported geometry entity type: ${String((exhaustive as { type?: string }).type)}`,
      );
    }
  }
}

/**
 * Resolve a DimensionStyle into a flat string-keyed property bag suitable
 * for the shell entity's `properties` map. The keys mirror the DIMVAR
 * identifiers that the SVG renderer (`PrimitiveAtlasStage.readDimStyle`)
 * looks up — without this enrichment the renderer always uses defaults
 * regardless of which style is applied.
 *
 * If the named style is missing (e.g. a stale reference) we fall through
 * to "Standard" or, as a last resort, return an empty bag so the renderer
 * uses its hard-coded defaults.
 */
export function resolveDimVarProperties(
  styleName: string,
  dimStylesByName: Map<string, DimensionStyle>,
): Record<string, string> {
  const style =
    dimStylesByName.get(styleName) ?? dimStylesByName.get("Standard");
  if (!style) return {};
  return {
    DIMASZ: String(style.DIMASZ),
    DIMTXT: String(style.DIMTXT),
    DIMGAP: String(style.DIMGAP),
    DIMEXO: String(style.DIMEXO),
    DIMEXE: String(style.DIMEXE),
    DIMSE1: String(style.DIMSE1),
    DIMSE2: String(style.DIMSE2),
    DIMCLRD: String(style.DIMCLRD),
    DIMCLRE: String(style.DIMCLRE),
    DIMDEC: String(style.DIMDEC),
    DIMPOST: style.DIMPOST,
    DIMBLK: style.DIMBLK,
    DIMBLK1: style.DIMBLK1,
    DIMBLK2: style.DIMBLK2,
    DIMTIH: String(style.DIMTIH),
    DIMRND: String(style.DIMRND),
    DIMLFAC: String(style.DIMLFAC),
    DIMLUNIT: String(style.DIMLUNIT),
    DIMTOL: String(style.DIMTOL),
    DIMLIM: String(style.DIMLIM),
    DIMTP: String(style.DIMTP),
    DIMTM: String(style.DIMTM),
    DIMTFAC: String(style.DIMTFAC),
    // Group C — annotative flag (0 = non-annotative, 1 = annotative).
    // Renderer reads this alongside entity.annotativeContexts[] to
    // decide whether to pick a scale-specific context.
    DIMANNO: String(style.DIMANNO),
    DIMSCALE: String(style.DIMSCALE),
  };
}

/**
 * MLEADERSTYLE property override flags — bitmask documented in the
 * AutoCAD MLEADERSTYLE Reference (group 90 inside MLEADER entity).
 * When a bit is set, the MLEADER entity carries an explicit override
 * for that field; otherwise the field should be read from the
 * referenced MLEADERSTYLE.
 *
 * The bit positions below match AutoCAD's published encoding. Only
 * the fields the renderer cares about are listed; the rest fall
 * through unchanged.
 * @public
 */
export const MLEADER_OVERRIDE_FLAGS = {
  LEADER_TYPE: 1 << 0,
  LEADER_LINE_COLOR: 1 << 1,
  LEADER_LINE_TYPE: 1 << 2,
  LEADER_LINE_WEIGHT: 1 << 3,
  ENABLE_LANDING: 1 << 4,
  LANDING_GAP: 1 << 5,
  ENABLE_DOGLEG: 1 << 6,
  DOGLEG_LENGTH: 1 << 7,
  ARROWHEAD: 1 << 8,
  ARROWHEAD_SIZE: 1 << 9,
  CONTENT_TYPE: 1 << 10,
  TEXT_STYLE: 1 << 11,
  TEXT_LEFT_ATTACHMENT: 1 << 12,
  TEXT_ANGLE: 1 << 13,
  TEXT_ALIGNMENT: 1 << 14,
  TEXT_COLOR: 1 << 15,
  TEXT_HEIGHT: 1 << 16,
  TEXT_FRAME: 1 << 17,
  ENABLE_USE_DEFAULT_MTEXT: 1 << 18,
  BLOCK_CONTENT_BLOCK: 1 << 19,
  BLOCK_CONTENT_COLOR: 1 << 20,
  BLOCK_CONTENT_SCALE: 1 << 21,
  BLOCK_CONTENT_ROTATION: 1 << 22,
  BLOCK_CONTENT_CONNECTION: 1 << 23,
  TEXT_RIGHT_ATTACHMENT: 1 << 24,
  TEXT_ATTACHMENT_DIRECTION: 1 << 25,
  TEXT_TOP_ATTACHMENT: 1 << 26,
  TEXT_BOTTOM_ATTACHMENT: 1 << 27,
} as const;

/**
 * Resolve a MultileaderStyle into a flat string-keyed property bag for
 * the renderer. Mirrors `resolveDimVarProperties`. The renderer reads
 * these keys via the entity's `properties` map; without this enrichment
 * it would always fall back to its hard-coded defaults.
 *
 * The bag intentionally uses MLVAR-style keys (CONTENT_TYPE, TEXT_HEIGHT,
 * ARROWHEAD_BLOCK, …) instead of pretty labels — these are internal
 * keys that never reach the user but live in the same string-bag as the
 * UI labels.
 *
 * Override flags semantics: if a bit in `propertyOverrideFlags` is set
 * the renderer should use the entity's own value (already encoded as a
 * UI label like "Text height"), otherwise it should fall back to the
 * style. This helper produces the style values regardless; the renderer
 * is responsible for choosing entity-vs-style when both are present.
 */
export function resolveMlVarProperties(
  styleName: string,
  mleaderStylesByName: Map<string, MultileaderStyle>,
): Record<string, string> {
  const style =
    mleaderStylesByName.get(styleName) ?? mleaderStylesByName.get("Standard");
  if (!style) return {};
  return {
    MLVAR_CONTENT_TYPE: String(style.contentType),
    MLVAR_TEXT_STYLE: style.textStyleName,
    MLVAR_TEXT_HEIGHT: String(style.textHeight),
    MLVAR_TEXT_COLOR: style.textColor,
    MLVAR_TEXT_ATTACHMENT_TYPE: String(style.textAttachmentType),
    MLVAR_TEXT_ATTACHMENT_DIRECTION: String(style.textAttachmentDirection),
    MLVAR_TEXT_LINE_SPACING_STYLE: String(style.textLineSpacingStyle),
    MLVAR_TEXT_LINE_SPACING_FACTOR: String(style.textLineSpacingFactor),
    MLVAR_TEXT_LINE_SPACING_DISTANCE: String(style.textLineSpacingDistance),
    MLVAR_TEXT_FRAME: String(style.textFrameDisplay),
    MLVAR_TEXT_WIDTH: String(style.textWidth),
    MLVAR_LEADER_TYPE: String(style.leaderType),
    MLVAR_LEADER_LINE_TYPE: style.leaderLineType,
    MLVAR_LEADER_COLOR: style.leaderColor,
    MLVAR_LEADER_WEIGHT: style.leaderWeight,
    MLVAR_LANDING_ENABLED: String(style.landingEnabled),
    MLVAR_DOGLEG_ENABLED: String(style.doglegEnabled),
    MLVAR_DOGLEG_LENGTH: String(style.doglegLength),
    MLVAR_LANDING_GAP: String(style.landingGap),
    MLVAR_MAX_LEADER_SEGMENTS: String(style.maxLeaderSegments),
    MLVAR_ARROWHEAD_SIZE: String(style.arrowheadSize),
    MLVAR_ARROWHEAD_BLOCK: style.arrowheadBlock,
    MLVAR_BLOCK_NAME: style.blockName,
    MLVAR_BLOCK_SCALE: String(style.blockScale),
    MLVAR_BLOCK_COLOR: style.blockColor,
    MLVAR_BLOCK_CONNECTION_TYPE: String(style.blockConnectionType),
    MLVAR_BLOCK_ROTATION: String(style.blockRotation),
    // PR-14 — style-level flag renamed to enableAnnotation (DXF group 296).
    // Preview key kept as MLVAR_ANNOTATIVE_ENABLED for renderer parity
    // (consumers in renderMultileader read the same key name).
    MLVAR_ANNOTATIVE_ENABLED: String(style.enableAnnotation),
    MLVAR_OVERALL_SCALE: String(style.overallScale),
    MLVAR_BREAK_ENABLED: String(style.breakEnabled),
    MLVAR_BREAK_GAP_SIZE: String(style.breakGapSize),
    MLVAR_BREAK_SIZE: String(style.breakSize),
  };
}

/**
 * Resolve the active annotative-context overrides for a multileader.
 * Returns an empty bag when annotation is disabled or no matching
 * context entry exists for the current scale.
 * @public
 */
export function resolveMlAnnotativeContextProperties(
  entity: MultileaderEntity,
  annotationScale: number | null,
): Record<string, string> {
  // PR-14 — entity-level "is annotative" is derived from contexts.length > 0
  // (no longer a stored boolean flag).
  if (annotationScale == null) return {};
  if (!entity.annotativeContexts || entity.annotativeContexts.length === 0) {
    return {};
  }
  const epsilon = 1e-6;
  const match =
    entity.annotativeContexts.find(
      (ctx) => Math.abs(ctx.scale - annotationScale) < epsilon,
    ) ??
    entity.annotativeContexts.find((ctx) => Math.abs(ctx.scale - 1) < epsilon);
  if (!match) return {};
  return {
    MLVAR_CTX_SCALE: String(match.scale),
    MLVAR_CTX_TEXT_HEIGHT: String(match.textHeight),
    MLVAR_CTX_ARROWHEAD_SIZE: String(match.arrowheadSize),
    MLVAR_CTX_DOGLEG_LENGTH: String(match.doglegLength),
    MLVAR_CTX_LANDING_GAP: String(match.landingGap),
    MLVAR_CTX_TEXT_ROTATION: String(match.textRotation),
    MLVAR_CTX_TEXT_COLOR: match.textColor,
    MLVAR_CTX_TEXT_WIDTH: String(match.textWidth),
  };
}

/**
 * Encode the leader-roots hierarchy as JSON for the renderer. The
 * structure is intentionally compact (numeric arrays, not labelled
 * objects) so the resulting string fits comfortably in a property
 * value. The renderer parses this on every draw — keep the schema
 * small.
 */
export function encodeLeaderRoots(roots: MleaderLeaderRoot[]): string {
  const compact = roots.map((root) => ({
    n: root.nodes.map((node) => ({
      d: node.doglegVector ? [node.doglegVector.x, node.doglegVector.y] : null,
      l: node.lines.map((line) => ({
        v: line.vertices.map((v) => [v.x, v.y]),
        b: (line.breakPoints ?? []).map((bp) => [
          bp.start.x,
          bp.start.y,
          bp.end.x,
          bp.end.y,
        ]),
        a: line.arrowheadBlock || "",
      })),
    })),
  }));
  return JSON.stringify(compact);
}

function buildLinePreview(entity: LineEntity): EntityPreview {
  // #317 residual R2 — surface the endpoint Z (group 30/31). Absent z ⇒ 0 by the
  // model contract (drawingPointOptionalZSchema; export does z ?? 0), keeping the
  // imported-line preview symmetric with the command-engine builder.
  const startZ = entity.start.z ?? 0;
  const endZ = entity.end.z ?? 0;
  const dz = endZ - startZ;
  // AutoCAD LINE "Length" is the 3-D endpoint distance now that Z is editable —
  // a pure-Z difference must report a non-zero length. Planar lines (dz = 0)
  // collapse to the previous 2-D hypot.
  const length = Math.hypot(
    entity.end.x - entity.start.x,
    entity.end.y - entity.start.y,
    dz,
  );
  const angle = Math.atan2(
    entity.end.y - entity.start.y,
    entity.end.x - entity.start.x,
  );

  return {
    id: entity.id,
    label: `Line ${entity.id}`,
    type: "Line",
    spaceId: entity.spaceId,
    summary: t("audit.imported_line"),
    editableProperties: [...lineEditableProperties],
    properties: {
      ...commonProperties(entity),
      "Start X": formatNumber(entity.start.x),
      "Start Y": formatNumber(entity.start.y),
      "Start Z": formatNumber(startZ),
      "End X": formatNumber(entity.end.x),
      "End Y": formatNumber(entity.end.y),
      "End Z": formatNumber(endZ),
      Length: formatNumber(length),
      Angle: formatNumber((angle * 180) / Math.PI),
    },
  };
}

function buildPointPreview(entity: PointEntity): EntityPreview {
  // S15 Slice B HARD PREREQ — surface the imported location Z (DXF group 30)
  // instead of the old hard-coded "0". The Properties panel re-dispatches its
  // FULL draft on every apply, so once Point routes through the typed writer
  // (geometryReducer Step 1d) a stale "0" bag value would be re-applied over
  // the real location.z and FLATTEN every elevated Point on the first property
  // apply of ANY field (even a Layer-only edit). Absent z ⇒ 0 by the model
  // contract (drawingPointOptionalZSchema; the bridge exports z ?? 0);
  // `formatNumber(0)` === "0", so a planar point is byte-identical. Mirrors
  // `buildCirclePreview` / `buildArcPreview` "Center Z" (S-ARC-CIRCLE-CENTER-Z).
  const locationZ = entity.location.z ?? 0;
  return {
    id: entity.id,
    label: `Point ${entity.id}`,
    type: "Point",
    spaceId: entity.spaceId,
    summary: t("audit.imported_point"),
    editableProperties: [...pointEditableProperties],
    properties: {
      ...commonProperties(entity),
      "Position X": formatNumber(entity.location.x),
      "Position Y": formatNumber(entity.location.y),
      "Position Z": formatNumber(locationZ),
      // Rotation is DXF group 50 = DEGREES (imported + exported verbatim) — show
      // it verbatim, identity like TEXT/MText. A ×180/π projection here silently
      // ~57× the exported group 50 (S15 Slice B P2).
      Rotation: formatNumber(entity.rotation),
    },
  };
}

function buildCirclePreview(entity: CircleEntity): EntityPreview {
  const diameter = entity.radius * 2;
  const circumference = Math.PI * diameter;
  const area = Math.PI * entity.radius * entity.radius;
  // S-ARC-CIRCLE-CENTER-Z — surface the imported center Z (DXF group 30). The
  // LEGACY live-render chain (entityToRenderable → circleToRenderable →
  // readCenterRaw `z: readNumber(properties,"Center Z")` → sampleCircle z) reads
  // the elevation FROM THIS BAG, so a hard-coded "0" planarised every elevated
  // circle. Absent z ⇒ 0 by the model contract (drawingPointOptionalZSchema; the
  // bridge exports z ?? 0). `formatNumber(0)` === "0", so a planar circle is
  // byte-identical — mirrors `buildLinePreview`'s `Start Z`/`End Z` surfacing.
  const centerZ = entity.center.z ?? 0;

  return {
    id: entity.id,
    label: `Circle ${entity.id}`,
    type: "Circle",
    spaceId: entity.spaceId,
    summary: t("audit.imported_circle"),
    editableProperties: [...circleEditableProperties],
    properties: {
      ...commonProperties(entity),
      "Center X": formatNumber(entity.center.x),
      "Center Y": formatNumber(entity.center.y),
      "Center Z": formatNumber(centerZ),
      Radius: formatNumber(entity.radius),
      Diameter: formatNumber(diameter),
      Circumference: formatNumber(circumference),
      Area: formatNumber(area),
    },
  };
}

function buildArcPreview(entity: ArcEntity): EntityPreview {
  const totalAngle = normalizeAngleSpan(entity.startAngle, entity.endAngle);
  const arcLength = (Math.PI * entity.radius * totalAngle) / 180;
  // S-ARC-CIRCLE-CENTER-Z — surface the imported center Z (DXF group 30); the
  // legacy live-render chain (arcToRenderable → readCenterRaw → sampleArc z)
  // reads it from THIS bag, so the prior hard-coded "0" planarised elevated
  // arcs. Absent z ⇒ 0 (drawingPointOptionalZSchema; export z ?? 0);
  // `formatNumber(0)` === "0" keeps a planar arc byte-identical. Mirrors LINE.
  const centerZ = entity.center.z ?? 0;

  return {
    id: entity.id,
    label: `Arc ${entity.id}`,
    type: "Arc",
    spaceId: entity.spaceId,
    summary: t("audit.imported_arc"),
    editableProperties: [...arcEditableProperties],
    properties: {
      ...commonProperties(entity),
      "Center X": formatNumber(entity.center.x),
      "Center Y": formatNumber(entity.center.y),
      "Center Z": formatNumber(centerZ),
      Radius: formatNumber(entity.radius),
      "Start angle": formatNumber(entity.startAngle),
      "End angle": formatNumber(entity.endAngle),
      "Total angle": formatNumber(totalAngle),
      "Arc length": formatNumber(arcLength),
    },
  };
}

function buildEllipsePreview(entity: EllipseEntity): EntityPreview {
  // AcDbEllipse (Autodesk DXF reference / ezdxf): 10/20 center, 11/21
  // major-axis endpoint VECTOR relative to center, 40 minor-to-major ratio,
  // 41/42 start/end parameters in RADIANS measured CCW from the major axis
  // (full ellipse: 0..2π). The bag keys below are the canonical set the
  // whole live pipeline already reads for command-drawn ellipses:
  //   - scene3d/adapters/ellipseToRenderable.ts — "Center X/Y" (readCenterRaw),
  //     "Major axis X/Y", "Ratio", "Start parameter", "End parameter";
  //   - scene/gripCalculation.ts "Ellipse" arm — same keys for the 5 AutoCAD
  //     grips (center + major± + minor±);
  //   - command-engine deriveEllipsePreviewProperties — the SAME key set, so
  //     imported and command-created ellipses are bag-identical.
  // SL-15 (round-5 F-B) — surface the 3D groups an imported ELEVATED (center.z ≠ 0)
  // / TILTED (major-axis z ≠ 0) / non-default-OCS ellipse carries, else a Z /
  // extrusion edit reaches `readEllipseState` with a missing group and FLATTENS it.
  // Delegate the CONDITIONAL "Center Z" / "Major axis Z" / "Extrusion X-Y-Z" rows to
  // the SAME command-engine helper the block-explode shell-preview uses
  // (`deriveEllipsePreviewProperties`) — one truth, no cloned conditional-Z logic
  // (dup NEW stays 0). The FULL `commonProperties(entity)` set (a superset of the
  // helper's `pickCommonProperties` five) is spread AFTER so nothing regresses. A
  // PLANAR default-OCS ellipse emits NONE of the conditional rows → byte-identical
  // (census unchanged). `extrusion` is normalised to `undefined` when it is the
  // (0,0,1) default so the helper omits the extrusion rows.
  // SL-15 round-10 @codex F1 (P1) — belt-and-suspenders: `EllipseEntity.extrusion`
  // is a REQUIRED schema default (0,0,1), but a promotion-built ellipse (Circle/Arc
  // → Ellipse in block explode) can reach here with `extrusion` genuinely `undefined`
  // at runtime because `assembleDrawingDocument`'s PROD fast-path skips the Zod parse
  // that injects the default. Reading `.x/.y/.z` off that `undefined` throws → the
  // whole preview build fails. The producer now sets (0,0,1) by construction
  // (blockReducerHelpers), but keep the `?? (0,0,1)` guard here so no reader can crash.
  const ellipseNormal = entity.extrusion ?? { x: 0, y: 0, z: 1 };
  const isDefaultExtrusion =
    ellipseNormal.x === 0 && ellipseNormal.y === 0 && ellipseNormal.z === 1;
  const ellipseGeometryProps = deriveEllipsePreviewProperties({
    id: entity.id,
    type: "Ellipse",
    spaceId: entity.spaceId,
    center: entity.center,
    majorAxisEndpoint: entity.majorAxisEndpoint,
    ratio: entity.ratio,
    startParam: entity.startParam,
    endParam: entity.endParam,
    ...(isDefaultExtrusion ? {} : { extrusion: ellipseNormal }),
  });
  return {
    id: entity.id,
    label: `Ellipse ${entity.id}`,
    type: "Ellipse",
    spaceId: entity.spaceId,
    summary: t("audit.imported_ellipse"),
    editableProperties: [...ellipseEditableProperties],
    properties: {
      ...ellipseGeometryProps,
      ...commonProperties(entity),
    },
  };
}

function buildSplinePreview(entity: SplineEntity): EntityPreview {
  // Serialize fit-points / control-points as semicolon-separated x,y
  // pairs so the SplineRenderer (and the property panel table editor)
  // can read them back without parsing JSON.
  const fitPointsStr = entity.fitPoints
    .map((p) => `${formatNumber(p.x)},${formatNumber(p.y)}`)
    .join(";");
  const controlPointsStr = entity.controlPoints
    .map((p) => `${formatNumber(p.x)},${formatNumber(p.y)}`)
    .join(";");
  const knotsStr = entity.knots.map((k) => formatNumber(k)).join(",");
  const weightsStr = entity.weights.map((w) => formatNumber(w)).join(",");

  return {
    id: entity.id,
    label: `Spline ${entity.id}`,
    type: "Spline",
    spaceId: entity.spaceId,
    summary: t("audit.imported_spline"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Degree",
      "Closed",
      "Rational",
      "Fit points",
      "Control points",
      "Knots",
      "Weights",
    ],
    properties: {
      ...commonProperties({
        layer: entity.layer,
        color: entity.color,
        linetype: entity.linetype,
        lineweight: entity.lineweight,
      }),
      Degree: String(entity.degree),
      Closed: entity.closed ? "Yes" : "No",
      Rational: entity.rational ? "Yes" : "No",
      "Fit points": fitPointsStr,
      "Control points": controlPointsStr,
      Knots: knotsStr,
      Weights: weightsStr,
      ...(entity.startTangent
        ? {
            "Start tangent X": formatNumber(entity.startTangent.x),
            "Start tangent Y": formatNumber(entity.startTangent.y),
            // Codex local review (batch-5 follow-up round 3): a 3-D
            // fit-point spline's explicit tangent normalizes by its FULL
            // XYZ magnitude in `fitPointsToControlPoints` (Codex P2-4,
            // #317) — omitting Z here left every consumer of THIS
            // property bag (scene hit-testing's own fit-spline rebuild)
            // reconstructing a tangent with an implicit Z=0, giving a
            // materially different normalization/XY-projection than the
            // real 3-D curve the renderer/TRIM path actually build.
            "Start tangent Z": formatNumber(entity.startTangent.z ?? 0),
          }
        : {}),
      ...(entity.endTangent
        ? {
            "End tangent X": formatNumber(entity.endTangent.x),
            "End tangent Y": formatNumber(entity.endTangent.y),
            "End tangent Z": formatNumber(entity.endTangent.z ?? 0),
          }
        : {}),
    },
  };
}

function buildLwpolylinePreview(entity: LwpolylineEntity): EntityPreview {
  return {
    id: entity.id,
    label: `Polyline ${entity.id}`,
    type: "LWPOLYLINE",
    spaceId: entity.spaceId,
    summary: t("audit.imported_polyline"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Constant width",
      "Closed",
      "Vertices",
    ],
    properties: {
      ...commonProperties(entity),
      "Constant width": formatNumber(entity.constantWidth ?? 0),
      // S15 Slice A review round — Elevation (DXF group 38) + plinegen (group
      // 70 bit 128) became REALLY-PERSISTING typed-writer labels, so the bag
      // must ALWAYS carry their keys: the properties/apply undo inverse
      // (`buildPropertiesApplyInverseFromSnapshots`) restores only keys
      // present in the PREV snapshot — a key INTRODUCED by an apply has no
      // prior value to restore (`optionalCommonKeyDefault` knows only
      // Linetype Scale / Thickness), which made a sole-Elevation / plinegen
      // edit un-undoable while Step 1d had already mutated the document.
      // Emitting the keys keeps prev/next symmetric (verbatim-restore branch)
      // and makes the MATCHPROP polyline-special plinegen copy live (the
      // resolution loop reads `sourceEntity.properties[label]`).
      Elevation: formatNumber(entity.elevation ?? 0),
      // Canonical "true"/"false" (same convention as Closed below); the
      // typed-descriptor display (descriptors/curves.ts) mirrors this form.
      "Linetype generation": entity.plinegen ? "true" : "false",
      // Canonical DXF/adapter form. Writing "Yes"/"No" here used to
      // leave `closed=false` in `lwpolylineToRenderable` because the
      // adapter compares the lowercased string to `"true"`.
      Closed: entity.closed ? "true" : "false",
      Vertices: entity.vertices
        .map((v) => `${formatNumber(v.x)},${formatNumber(v.y)}`)
        .join(";"),
      // Codex P1-B — surface the per-vertex bulge list so the property
      // panel + grip render path (gripCalculation parseBulgeList) and a
      // round-trip are consistent with the arc segments. The AUTHORITATIVE
      // source for vertex-drag persistence is the live document entity's
      // bulge array (see geometryReducer / propertyTranslator), NOT this
      // formatted preview string — but emitting it keeps the panel honest
      // and lets the seg-mid drag measure from the correct arc midpoint.
      Bulges: entity.vertices
        .map((v) => formatNumber(v.bulge ?? 0))
        .join(";"),
    },
  };
}

/**
 * Map DXF group codes 72 (horizontal justification) and 73 (vertical
 * justification) to the AutoCAD DXF reference `Justify` token used in the
 * TEXT entity's properties panel.
 *
 * Reference: AutoCAD 2022 DXF Reference → TEXT group codes.
 *
 * | v \ h | 0        | 1        | 2        | 3        | 4        | 5       |
 * |-------|----------|----------|----------|----------|----------|---------|
 * | 0     | LEFT     | CENTER   | RIGHT    | ALIGNED  | MIDDLE   | FIT     |
 * | 1     | BL       | BC       | BR       | —        | —        | —       |
 * | 2     | ML       | MC       | MR       | —        | —        | —       |
 * | 3     | TL       | TC       | TR       | —        | —        | —       |
 *
 * Unknown combinations fall back to `"LEFT"` (DXF default when 72=73=0).
 */
export function justifyFromCodes(h: number, v: number): string {
  if (v === 0) {
    switch (h) {
      case 0:
        return "LEFT";
      case 1:
        return "CENTER";
      case 2:
        return "RIGHT";
      case 3:
        return "ALIGNED";
      case 4:
        return "MIDDLE";
      case 5:
        return "FIT";
      default:
        return "LEFT";
    }
  }
  if (v === 1) {
    if (h === 0) return "BL";
    if (h === 1) return "BC";
    if (h === 2) return "BR";
    return "LEFT";
  }
  if (v === 2) {
    if (h === 0) return "ML";
    if (h === 1) return "MC";
    if (h === 2) return "MR";
    return "LEFT";
  }
  if (v === 3) {
    if (h === 0) return "TL";
    if (h === 1) return "TC";
    if (h === 2) return "TR";
    return "LEFT";
  }
  return "LEFT";
}

export function buildTextPreview(entity: TextEntity): EntityPreview {
  const hJust = entity.hJustification ?? 0;
  const vJust = entity.vJustification ?? 0;
  const alignX = entity.alignmentPoint?.x ?? entity.insertionPoint.x;
  const alignY = entity.alignmentPoint?.y ?? entity.insertionPoint.y;
  // PR-3 — serialize annotativeContexts so the renderer's
  // useCannoscaleResolver can decode them per scale switch without
  // re-walking the DrawingDocument. Empty array is encoded as "[]" so
  // decodeAnnotativeContextsJson returns a stable empty list (and the
  // renderer's non-annotative fast path applies).
  const annotativeContextsJson = JSON.stringify(
    entity.annotativeContexts ?? [],
  );
  return {
    id: entity.id,
    label: `Text ${entity.id}`,
    type: "Text",
    spaceId: entity.spaceId,
    // local-Codex-review P3 — this builder is exported (unlike its 14
    // siblings) and called directly by tests, so it must apply real
    // provenance itself rather than relying on `buildShellEntityPreview`'s
    // wrapper override (which still runs unchanged on the normal path —
    // same value, computed twice is harmless).
    summary: resolveEntityProvenanceSummary(entity),
    editableProperties: [
      "Layer",
      "Color",
      "Contents",
      "Text style",
      "Height",
      "Rotation",
      "Justify",
      "Width factor",
      "Oblique angle",
    ],
    properties: {
      ...commonProperties(entity),
      Contents: entity.contents,
      "Text style": entity.styleName ?? "Standard",
      Height: formatNumber(entity.height),
      Rotation: formatNumber(entity.rotation ?? 0),
      Justify: justifyFromCodes(hJust, vJust),
      "Width factor": formatNumber(entity.widthFactor ?? 1),
      "Oblique angle": formatNumber(entity.obliqueAngle ?? 0),
      "Generation flags": formatNumber(entity.generationFlags ?? 0),
      "Alignment X": formatNumber(alignX),
      "Alignment Y": formatNumber(alignY),
      "Insertion X": formatNumber(entity.insertionPoint.x),
      "Insertion Y": formatNumber(entity.insertionPoint.y),
      ...(entity.creationScaleName !== undefined
        ? { "Creation scale": entity.creationScaleName }
        : {}),
      __entity_annotative_contexts: annotativeContextsJson,
    },
  };
}

export function buildMTextPreview(entity: MtextEntity): EntityPreview {
  // PR-3 — see buildTextPreview for rationale.
  const annotativeContextsJson = JSON.stringify(
    entity.annotativeContexts ?? [],
  );
  return {
    id: entity.id,
    label: `MText ${entity.id}`,
    type: "MText",
    spaceId: entity.spaceId,
    // local-Codex-review P3 — see buildTextPreview's comment above.
    summary: resolveEntityProvenanceSummary(entity),
    editableProperties: [
      "Layer",
      "Color",
      "Contents",
      "Text style",
      "Height",
      "Width",
      "Background fill",
    ],
    properties: {
      ...commonProperties(entity),
      Contents: entity.contents,
      "Text style": entity.styleName ?? "Standard",
      Height: formatNumber(entity.nominalHeight),
      Width: formatNumber(entity.referenceWidth),
      "Background fill": entity.backgroundFill ? "On" : "Off",
      Attachment: formatNumber(entity.attachmentPoint ?? 1),
      "Line spacing factor": formatNumber(entity.lineSpacingFactor ?? 1),
      // `Rotation` stays the RAW group-50 rotation attribute — it is the value the
      // Property Grid displays AND writes back to `entity.rotation`
      // (propertyTranslator MText map). Keeping it raw preserves property-edit
      // round-trip integrity; the EFFECTIVE render angle (Finding A) is carried
      // separately in the render-only "__mtext_render_angle" key below so the two
      // seams (property-edit round-trip vs render placement) never conflate.
      Rotation: formatNumber(entity.rotation ?? 0),
      "Background color": entity.backgroundFillColor ?? "",
      "Background transparency": formatNumber(
        entity.backgroundTransparency ?? 0,
      ),
      "Fill box scale": formatNumber(entity.fillBoxScale ?? 1.5),
      // Codex #342 F1+F3 — OCS extrusion Z (group 230), preserved for any
      // consumer that reads the scalar. Absent extrusion ⇒ (0,0,1) default ⇒ z=1.
      "Extrusion Z": formatNumber(entity.extrusion?.z ?? 1),
      // Codex #342 round-8 Finding A — RENDER-ONLY effective placement angle. A
      // custom group-11 X-axis direction WINS over the `rotation` attribute
      // (ezdxf get_rotation = atan2(dir.y,dir.x) precedence), so the legacy bag
      // render path (mtextToRenderable) must place the glyph run at THIS angle,
      // not the raw `Rotation` above. The `__`-prefixed key marks it
      // render-internal (NOT a Property-Grid-editable field, NOT a round-trip
      // value — the DXF WRITER consumes the typed model's group 50 + 11/21/31,
      // so round-7 export self-inverse is untouched). Non-custom (default
      // {1,0,0}) ⇒ equals `rotation` ⇒ byte-identical golden parity.
      __mtext_render_angle: formatNumber(
        effectiveMTextAngleDeg(entity.xAxisDirection, entity.rotation ?? 0),
      ),
      // Codex #342 round-8 Finding B — RENDER-ONLY authoritative mirror flag,
      // derived from the FULL OCS normal via the shared `isReflectedOcs`
      // predicate (the bag carries no extrusion X/Y, so the producer computes the
      // one-truth boolean here and the legacy render reader consumes it directly
      // rather than re-running a brittle `z < 0`). Default/absent ⇒ "0" ⇒ not
      // mirrored (golden parity); a mirrored default MTEXT ⇒ (0,0,−1) ⇒ "1".
      __mtext_render_mirrored: isReflectedOcs(entity.extrusion) ? "1" : "0",
      "Insertion X": formatNumber(entity.insertionPoint.x),
      "Insertion Y": formatNumber(entity.insertionPoint.y),
      ...(entity.creationScaleName !== undefined
        ? { "Creation scale": entity.creationScaleName }
        : {}),
      __entity_annotative_contexts: annotativeContextsJson,
    },
  };
}

const DIMENSION_SUBTYPE_LABELS: Record<number, string> = {
  0: "Linear",
  1: "Aligned",
  2: "Angular",
  3: "Diameter",
  4: "Radius",
  5: "Angular 3pt",
  6: "Ordinate",
};

function dimensionSubtypeLabel(dimensionType: number): string {
  const subtype = dimensionType & 0x0f;
  return DIMENSION_SUBTYPE_LABELS[subtype] ?? `Unknown (${subtype})`;
}

function buildDimensionPreview(
  entity: DimensionEntity,
  dimStylesByName: Map<string, DimensionStyle>,
): EntityPreview {
  const ext1 = entity.extLine1Point ?? { x: 0, y: 0 };
  const ext2 = entity.extLine2Point ?? { x: 0, y: 0 };
  const defPt = entity.defPoint ?? { x: 0, y: 0 };
  const textMid = entity.textMidpoint ?? { x: 0, y: 0 };
  const dimType = entity.dimensionType ?? 0;
  const styleName = entity.styleName ?? "Standard";
  // Resolve every DIMVAR from the referenced dimension style so the
  // renderer's `readDimStyle(properties)` can pick them up. Without this
  // the renderer always falls back to default DIMVARs and visual changes
  // (DIMTXT, DIMBLK1, DIMDEC, etc.) never reach the user.
  const dimVarProperties = resolveDimVarProperties(styleName, dimStylesByName);
  // Group C — serialize the per-scale annotative context snapshots so
  // the renderer can pick the active one without another document
  // round-trip. Matches the MLEADER pattern (`__mleader_annotative_contexts`).
  const annotativeContextsJson = JSON.stringify(
    entity.annotativeContexts ?? [],
  );

  return {
    id: entity.id,
    label: `Dimension ${entity.id}`,
    type: "Dimension",
    spaceId: entity.spaceId,
    summary: t("audit.imported_dimension"),
    editableProperties: [
      "Layer",
      "Color",
      "Dimension style",
      "Measurement",
      "Text override",
    ],
    properties: {
      ...commonProperties(entity),
      ...dimVarProperties,
      Subtype: dimensionSubtypeLabel(dimType),
      "Dimension style": styleName,
      Measurement: formatNumber(entity.measurement ?? 0),
      "Text override": entity.dimensionText || "<>",
      "Text height": formatNumber(entity.textHeight ?? 0),
      "Text rotation": formatNumber(entity.textRotation ?? 0),
      // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the real
      // DXF group-53 text_rotation override (`DimensionRawFields
      // .textRotationOverride`'s doc has the group-52-vs-53 rationale),
      // now consumed by `dimensionToPrimitives.ts` (the PDF export path)
      // so a MIRRTEXT-mirrored (or directly authored) dimension's text
      // angle survives into the printed PDF, not just the live viewport.
      "Text angle relative to dim line": formatNumber(
        entity.textAngleRelativeToDimLine ?? 0,
      ),
      "Text mirrored": entity.textMirrored ? "1" : "0",
      "Extension 1 X": formatNumber(ext1.x),
      "Extension 1 Y": formatNumber(ext1.y),
      "Extension 2 X": formatNumber(ext2.x),
      "Extension 2 Y": formatNumber(ext2.y),
      "Dimension line X": formatNumber(defPt.x || textMid.x),
      "Dimension line Y": formatNumber(defPt.y || textMid.y),
      "Third point X": entity.thirdPoint
        ? formatNumber(entity.thirdPoint.x)
        : "",
      "Third point Y": entity.thirdPoint
        ? formatNumber(entity.thirdPoint.y)
        : "",
      "Arc point X": entity.arcPoint ? formatNumber(entity.arcPoint.x) : "",
      "Arc point Y": entity.arcPoint ? formatNumber(entity.arcPoint.y) : "",
      "Oblique angle": formatNumber(entity.obliqueAngle ?? 0),
      "Leader length": formatNumber(entity.leaderLength ?? 0),
      Angle: formatNumber(entity.linearAngle ?? 0),
      __dimension_annotative_contexts: annotativeContextsJson,
    },
  };
}

function buildMultileaderPreview(entity: MultileaderEntity): EntityPreview {
  // Extract arrow/landing from first root's first node's first line
  const firstRoot = entity.leaderRoots[0];
  const firstNode = firstRoot?.nodes[0];
  const firstLine = firstNode?.lines[0];
  const firstVertex = firstLine?.vertices[0];
  const arrowX = firstVertex ? String(firstVertex.x) : "0";
  const arrowY = firstVertex ? String(firstVertex.y) : "0";

  // Real leader-line count = sum across roots → nodes → lines. The
  // single-leader display value (used by older smoke tests) tracks
  // exactly the number of arrow tips.
  const leaderLineCount = entity.leaderRoots.reduce(
    (sum, root) =>
      sum + root.nodes.reduce((s, node) => s + node.lines.length, 0),
    0,
  );

  // Encode the full leader hierarchy as JSON so the renderer can walk
  // every root → node → line without an extra round-trip to the
  // geometry document. The key is intentionally prefixed with `__` to
  // mark it as renderer-internal.
  const leaderRootsJson = encodeLeaderRoots(entity.leaderRoots);
  const blockAttrJson = JSON.stringify(entity.blockAttributeReferences ?? []);
  const annotativeContextsJson = JSON.stringify(
    entity.annotativeContexts ?? [],
  );

  return {
    id: entity.id,
    label: `MLeader ${entity.id}`,
    type: "Multileader",
    spaceId: entity.spaceId,
    summary: t("audit.imported_multileader"),
    editableProperties: ["Layer", "Color", "Multileader style", "Attachment"],
    properties: {
      ...commonProperties(entity),
      "Multileader style": entity.styleName ?? "Standard",
      Attachment: "Top left",
      "Arrow X": arrowX,
      "Arrow Y": arrowY,
      "Landing X": String(entity.textLocation?.x ?? 0),
      "Landing Y": String(entity.textLocation?.y ?? 0),
      "Leader count": String(leaderLineCount),
      // ── Entity-level overrides (consumed by renderer) ──
      MLENT_CONTENT_TYPE: String(entity.contentType),
      MLENT_TEXT_CONTENTS: entity.textContents,
      MLENT_TEXT_HEIGHT: String(entity.textHeight),
      MLENT_TEXT_ROTATION: String(entity.textRotation),
      // GitHub Codex review PR #558 (post-rebase fresh pass) P2 —
      // handedness companion to MLENT_TEXT_ROTATION, now consumed by
      // `multileaderToPrimitives.ts` (the PDF export path) so a
      // MIRRTEXT=1-mirrored MLeader's glyph handedness survives into the
      // printed PDF, not just the live viewport.
      MLENT_TEXT_MIRRORED: entity.textMirrored ? "1" : "0",
      MLENT_TEXT_COLOR: entity.textColor,
      MLENT_TEXT_ATTACHMENT_TYPE: String(entity.textAttachmentType),
      MLENT_TEXT_ATTACHMENT_DIRECTION: String(entity.textAttachmentDirection),
      MLENT_TEXT_FRAME: String(entity.textFrameDisplay),
      MLENT_TEXT_LINE_SPACING_STYLE: String(entity.textLineSpacingStyle),
      MLENT_TEXT_LINE_SPACING_FACTOR: String(entity.textLineSpacingFactor),
      MLENT_TEXT_LINE_SPACING_DISTANCE: String(entity.textLineSpacingDistance),
      MLENT_TEXT_WIDTH: String(entity.textWidth),
      MLENT_LEADER_TYPE: String(entity.leaderLineType),
      MLENT_LEADER_COLOR: entity.leaderLineColor,
      MLENT_LEADER_WEIGHT: entity.leaderLineWeight,
      MLENT_LANDING_ENABLED: String(entity.landingEnabled),
      MLENT_DOGLEG_ENABLED: String(entity.doglegEnabled),
      MLENT_DOGLEG_LENGTH: String(entity.doglegLength),
      MLENT_LANDING_GAP: String(entity.landingGap),
      MLENT_ARROWHEAD_SIZE: String(entity.arrowheadSize),
      MLENT_ARROWHEAD_BLOCK: entity.arrowheadBlock,
      MLENT_BLOCK_NAME: entity.blockName,
      MLENT_BLOCK_SCALE: String(entity.blockScale),
      MLENT_BLOCK_COLOR: entity.blockColor,
      MLENT_BLOCK_CONNECTION_TYPE: String(entity.blockConnectionType ?? 0),
      MLENT_BLOCK_ROTATION: String(entity.blockRotation),
      MLENT_BREAK_SIZE: String(entity.breakSize),
      MLENT_PROPERTY_OVERRIDE_FLAGS: String(entity.propertyOverrideFlags),
      // PR-14 — entity-level "is annotative" is derived from contexts.length > 0,
      // not a stored bool. Preview key name preserved for renderer parity.
      MLENT_ANNOTATIVE_ENABLED: String(
        (entity.annotativeContexts?.length ?? 0) > 0,
      ),
      __mleader_leader_roots: leaderRootsJson,
      __mleader_block_attrs: blockAttrJson,
      __mleader_annotative_contexts: annotativeContextsJson,
    },
  };
}

function buildRevisionCloudPreview(entity: RevisionCloudEntity): EntityPreview {
  // PR-D — emit Corner 1/2 X/Y so the existing SVG renderer's
  // describeRevisionCloud helper continues to see them. AutoCAD
  // documents revision-cloud as a polyline plus arc list, so the
  // proper renderer also needs the full vertex array — encoded as
  // JSON for the Three.js adapter.
  const verticesJson = JSON.stringify(entity.vertices ?? []);
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const v of entity.vertices ?? []) {
    if (v.x < minX) minX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.x > maxX) maxX = v.x;
    if (v.y > maxY) maxY = v.y;
  }
  const hasBounds = Number.isFinite(minX);
  return {
    id: entity.id,
    label: `Revision cloud ${entity.id}`,
    type: "Revision Cloud",
    spaceId: entity.spaceId,
    summary: t("audit.imported_revcloud"),
    editableProperties: [
      "Layer",
      "Color",
      "Cloud style",
      "Approximate arc length",
    ],
    properties: {
      ...commonProperties(entity),
      "Cloud style": entity.revisionCloudStyle ?? "Normal",
      // REVCLOUD-MODES — project the typed creationMode so the shell-built
      // 'Creation mode' row survives the syncShellStateFromGeometryDocument
      // preview rebuild (the typed model carries it; this is the projection
      // layer). Read-only until REVCLOUD-PROPS-EDITOR.
      "Creation mode": entity.creationMode ?? "Rectangular",
      "Approximate arc length": formatNumber(entity.approximateArcLength ?? 15),
      "Corner 1 X": formatNumber(hasBounds ? minX : 0),
      "Corner 1 Y": formatNumber(hasBounds ? minY : 0),
      "Corner 2 X": formatNumber(hasBounds ? maxX : 0),
      "Corner 2 Y": formatNumber(hasBounds ? maxY : 0),
      __revcloud_vertices: verticesJson,
      Closed: entity.closed ? "true" : "false",
    },
  };
}

// WIPEOUT property bag — boundary vertex count + frame visibility flag
// shown in the property palette. The vertex coordinates are keyed
// Vertex 1 X / Vertex 1 Y / … so the renderer can reconstruct the
// polygon outline from the property map alone (mirrors how Hatch +
// LWPOLYLINE expose vertex coordinates).
function buildWipeoutPreview(entity: WipeoutEntity): EntityPreview {
  const vertexProperties: Record<string, string> = {};
  entity.vertices.forEach((v, idx) => {
    vertexProperties[`Vertex ${idx + 1} X`] = formatNumber(v.x);
    vertexProperties[`Vertex ${idx + 1} Y`] = formatNumber(v.y);
  });
  return {
    id: entity.id,
    label: `Wipeout ${entity.id}`,
    type: "Wipeout",
    spaceId: entity.spaceId,
    summary: t("audit.imported_wipeout"),
    editableProperties: ["Layer", "Color", "Frame", "Vertex count"],
    properties: {
      ...commonProperties(entity),
      Frame: entity.frame ? "On" : "Off",
      "Vertex count": String(entity.vertices.length),
      ...vertexProperties,
    },
  };
}

/** AutoCAD's Properties-palette header for each heavy-polyline group-70 subtype. */
const POLYLINE_TYPE_LABEL: Record<
  ReturnType<typeof polylineSubtype>,
  string
> = {
  "2d": "2D Polyline",
  "3d": "3D Polyline",
  "polygon-mesh": "Polygon Mesh",
  "polyface-mesh": "Polyface Mesh",
};

/** DXF group 75 — curves and smooth-surface type, verbatim from the reference. */
const POLYLINE_SURFACE_TYPE_LABEL: Record<number, string> = {
  0: "None",
  5: "Quadratic B-spline",
  6: "Cubic B-spline",
  8: "Bezier",
};

/**
 * Legacy heavy POLYLINE preview bag.
 *
 * Every key here has a matching typed descriptor
 * (`command-engine/descriptors/legacyPolylineTableOle.ts`) — the display forms
 * are mirrored from it EXACTLY (the "true"/"false" booleans follow the same
 * convention LWPOLYLINE's Closed / Linetype generation already use, so a
 * MATCHPROP copy between the two polyline families reads one convention).
 *
 * The mesh rows are emitted for every subtype and read empty on a plain
 * polyline, matching AutoCAD's palette.
 */
function buildPolylinePreview(entity: PolylineEntity): EntityPreview {
  const surfaceType =
    entity.surfaceType === undefined
      ? ""
      : (POLYLINE_SURFACE_TYPE_LABEL[entity.surfaceType] ??
        String(entity.surfaceType));
  // Length / Area come from the ONE subtype-aware path-metric truth
  // (`command-engine/heavyPolylinePathMetrics.ts`), which the typed descriptor
  // (`descriptors/legacyPolylineTableOle.ts`) projects too — one truth, two
  // projections, so this bag and the palette's typed read cannot disagree.
  //
  // @codex PR #785 round 5, FINDING B — this used to hand the RAW VERTEX RECORD
  // array to the shared helpers, which is not a path for a polygon mesh (an M×N
  // grid: the walk counted links between row ends), for a poly-face mesh (which
  // additionally interleaves FACE records whose 10/20/30 is FILLER) or for the
  // spline-fit frame records of a bit-4 polyline. A mesh has NO path metric at
  // all, so both rows read EMPTY for one — never `0`, which would read as a
  // measured value. A 3D polyline has a Length but NO Area (round 6): its
  // closing polygon is in general non-planar, so its Area row reads empty too.
  // See that module's header for the AutoCAD reference.
  //
  // CHORD-based and 3D-aware for a 3D polyline: both properties belong to the
  // shared helpers and are documented there.
  const pathMetrics = heavyPolylinePathMetrics(entity);
  return {
    id: entity.id,
    label: `Polyline ${entity.id}`,
    type: "Polyline",
    spaceId: entity.spaceId,
    summary: t("audit.imported_heavy_polyline"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Closed",
      "Linetype generation",
      "Start segment width",
      "End segment width",
      "Elevation",
    ],
    properties: {
      ...commonProperties(entity),
      "Polyline type": POLYLINE_TYPE_LABEL[polylineSubtype(entity)],
      Vertices: entity.vertices
        .map((v) => `${formatNumber(v.location.x)},${formatNumber(v.location.y)}`)
        .join(";"),
      Closed: polylineIsClosed(entity) ? "true" : "false",
      "Linetype generation":
        (entity.flags & DXF_POLYLINE_FLAG.CONTINUOUS_LINETYPE) !== 0
          ? "true"
          : "false",
      "Start segment width": formatNumber(entity.defaultStartWidth ?? 0),
      "End segment width": formatNumber(entity.defaultEndWidth ?? 0),
      Elevation: formatNumber(entity.elevation.z ?? 0),
      Length:
        pathMetrics === undefined ? "" : formatNumber(pathMetrics.length),
      // `pathMetrics?.area` is undefined for TWO reasons and both render the
      // same EMPTY row: a mesh has no path at all, and a 3D polyline has a
      // path but no defined planar area (@codex PR #785 round 6). Reading the
      // field rather than only the pair is what keeps this projection honest
      // for the second case.
      Area:
        pathMetrics?.area === undefined ? "" : formatNumber(pathMetrics.area),
      "M vertex count":
        entity.meshMCount === undefined ? "" : formatNumber(entity.meshMCount),
      "N vertex count":
        entity.meshNCount === undefined ? "" : formatNumber(entity.meshNCount),
      "Smooth surface M density":
        entity.smoothSurfaceMDensity === undefined
          ? ""
          : formatNumber(entity.smoothSurfaceMDensity),
      "Smooth surface N density":
        entity.smoothSurfaceNDensity === undefined
          ? ""
          : formatNumber(entity.smoothSurfaceNDensity),
      "Surface type": surfaceType,
    },
  };
}

/**
 * ACAD_TABLE preview bag.
 *
 * `Table style` is the REAL name read out of the referenced TABLESTYLE object
 * when the caller supplies the document's retained OBJECTS groups — never a
 * hard-coded "Standard" (CLAUDE.md ПРИОРИТЕТ #1). It is a bag-sourced UI
 * projection (the name lives in the OBJECTS graph, not on the entity), so its
 * descriptor is `ui-projection-deferred` and the key is listed in
 * `SINGLE_SELECT_BAG_RESIDUAL` — the same shape the INSERT's
 * BLOCK_RECORD-sourced rows use.
 *
 * Cell contents / styles / merges and table breaking are NOT surfaced: they
 * are the deferred table-editing feature, and a row with no typed home behind
 * it would be a placeholder.
 */
function buildTablePreview(
  entity: TableEntity,
  rawObjectGroups?: readonly { code: number; value: string }[],
): EntityPreview {
  return {
    id: entity.id,
    label: `Table ${entity.id}`,
    type: "Table",
    spaceId: entity.spaceId,
    summary: t("audit.imported_table"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Position X",
      "Position Y",
      "Position Z",
    ],
    properties: {
      ...commonProperties(entity),
      "Table style": resolveTableStyleName(rawObjectGroups, entity.tableStyleHandle) ?? "",
      Rows: entity.rowCount === undefined ? "" : formatNumber(entity.rowCount),
      Columns:
        entity.columnCount === undefined ? "" : formatNumber(entity.columnCount),
      "Table width": formatNumber(
        entity.columnWidths.reduce((sum, w) => sum + w, 0),
      ),
      "Table height": formatNumber(
        entity.rowHeights.reduce((sum, h) => sum + h, 0),
      ),
      "Position X": formatNumber(entity.insertionPoint.x),
      "Position Y": formatNumber(entity.insertionPoint.y),
      "Position Z": formatNumber(entity.insertionPoint.z ?? 0),
      Rotation: formatNumber(tableRotationDegrees(entity)),
    },
  };
}

/** DXF group 71 — OLE object type. */
const OLE_OBJECT_TYPE_LABEL: Record<number, string> = {
  1: "Link",
  2: "Embedded",
  3: "Static",
};

/** DXF group 73 — OLE draw aspect. */
const OLE_DRAW_ASPECT_LABEL: Record<number, string> = {
  1: "Content",
  2: "Thumbnail",
  3: "Icon",
  4: "Doc print",
};

/**
 * OLE2FRAME preview bag — the frame geometry plus the OLE record's own
 * descriptors.
 *
 * The embedded payload is never decoded (see `oleFrameEntitySchema`), so
 * `Data size` reports the retained byte count and nothing claims to describe
 * the picture itself.
 */
function buildOleFramePreview(entity: OleFrameEntity): EntityPreview {
  return {
    id: entity.id,
    label: `OLE ${entity.id}`,
    type: "OLE Frame",
    spaceId: entity.spaceId,
    summary: t("audit.imported_ole_frame"),
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Upper left X",
      "Upper left Y",
      "Lower right X",
      "Lower right Y",
    ],
    properties: {
      ...commonProperties(entity),
      "OLE object type":
        entity.oleObjectType === undefined
          ? (entity.objectDescription ?? "")
          : (OLE_OBJECT_TYPE_LABEL[entity.oleObjectType] ??
            String(entity.oleObjectType)),
      "OLE version":
        entity.oleVersion === undefined ? "" : formatNumber(entity.oleVersion),
      "Draw aspect":
        entity.drawAspect === undefined
          ? ""
          : (OLE_DRAW_ASPECT_LABEL[entity.drawAspect] ??
            String(entity.drawAspect)),
      "Upper left X": formatNumber(entity.upperLeft.x),
      "Upper left Y": formatNumber(entity.upperLeft.y),
      "Lower right X": formatNumber(entity.lowerRight.x),
      "Lower right Y": formatNumber(entity.lowerRight.y),
      Width: formatNumber(Math.abs(entity.lowerRight.x - entity.upperLeft.x)),
      Height: formatNumber(Math.abs(entity.upperLeft.y - entity.lowerRight.y)),
      "Data size":
        entity.binaryByteCount === undefined
          ? ""
          : formatNumber(entity.binaryByteCount),
    },
  };
}

/**
 * Slice 2 — resolve a gradient hatch's ramp into the {@link SceneGradient}-shaped
 * JSON the scene adapter reads (`__hatch_gradient`). Mirrors the PDF
 * `resolveGradientSpec` but with FAITHFUL scene colours (no white→black tweak)
 * and emits `#rrggbb` strings. Returns `null` for a non-gradient hatch
 * (`gradient` absent or `kind !== 1`) so the channel is OMITTED — the existing
 * solid / pattern paths stay byte-unchanged (edge case 13).
 */
function resolveSceneGradient(entity: HatchEntity): string | null {
  const g = entity.gradient;
  if (g === undefined || g.kind !== 1) return null;
  // Entity fallback colour: an ACI-resolved entity colour would need the full
  // BYLAYER chain; gradient stops in real AutoCAD always carry their own colour,
  // so a defensive black fallback is sufficient (only hit by a stop missing both
  // aci + trueColor, which AutoCAD never writes).
  const ramp = resolveGradientRamp(g, { r: 0, g: 0, b: 0 }, SCENE_STOP_RESOLVERS);
  const sceneGradient = {
    name: ramp.name,
    shadingType: ramp.shadingType,
    inverse: ramp.inverse,
    symmetric: ramp.symmetric,
    curved: ramp.curved,
    rotation: ramp.rotation,
    shift: ramp.shift,
    color0Hex: rgbToHex(ramp.color0),
    color1Hex: rgbToHex(ramp.color1),
  };
  return JSON.stringify(sceneGradient);
}

function buildHatchPreview(entity: HatchEntity): EntityPreview {
  // PR-3 — see buildTextPreview for rationale.
  const annotativeContextsJson = JSON.stringify(
    entity.annotativeContexts ?? [],
  );
  // PR-D — serialize boundary loops so the Three.js renderer can build
  // a tessellated fill mesh (apps/web/src/app/scene3d/adapters/
  // hatchToRenderable.ts). The SVG renderer continues to use
  // entity.properties.Boundary for its single-loop polygon fill; the
  // 3D renderer needs the full multi-loop edge graph (line / arc /
  // ellipticArc), so we encode it as JSON in a private property to
  // avoid surfacing it in the property palette.
  const boundaryPathsJson = JSON.stringify(entity.boundaryPaths ?? []);
  // Slice 2 — gradient hatches (DXF 450=1) carry their resolved colour ramp in a
  // private `__hatch_gradient` channel (mirrors __hatch_boundary_paths). OMITTED
  // for solid / pattern hatches so their preview is byte-unchanged.
  const gradientJson = resolveSceneGradient(entity);
  // PR-D — also encode Solid/PatternAngle/Elevation so the renderer
  // does not need to look them up by string. These are exposed as
  // visible properties (no underscore prefix) since AutoCAD shows them
  // in the property palette anyway.
  return {
    id: entity.id,
    label: `Hatch ${entity.id}`,
    type: "Hatch",
    spaceId: entity.spaceId,
    summary: t("audit.imported_hatch"),
    editableProperties: [
      "Layer",
      "Color",
      "Pattern",
      "Scale",
      "Associative",
      // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR — advertised editable like the
      // sibling Hatch booleans; the typed writer enforces the AutoCAD
      // per-instance gate (Pattern type = User defined) at the write
      // boundary, so a no-op edit is rejected rather than pre-filtered here.
      "Double",
    ],
    properties: {
      ...commonProperties(entity),
      Pattern: entity.patternName ?? "ANSI31",
      Scale: formatNumber(entity.patternScale ?? 1),
      Angle: formatNumber(entity.patternAngle ?? 0),
      Solid: entity.solidFill ? "Yes" : "No",
      Elevation: formatNumber(entity.elevation ?? 0),
      Associative: entity.associative ? "Yes" : "No",
      // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2) — "Pattern type" was
      // previously ABSENT from this import-path bag (present only in the
      // command-engine CREATE factory's `deriveHatchPreviewProperties`,
      // `hatchPreviewProperties`), a pre-existing both-factories asymmetry
      // that left the row invisible for every IMPORTED hatch. Added here as a
      // required prerequisite (NOT scope creep): "Double" is read-only unless
      // Pattern type = User defined, and the SHELL mirror
      // (`typedExtendedGeometryShellRejects`'s `readOnlyFromBag`) decides
      // that per-instance gate FROM this exact bag cell (no live typed entity
      // at the shell layer) — without it every imported hatch's "Double" gate
      // would silently degrade to the pre-existing accept-into-bag behaviour
      // the shell↔document divergence fix exists to close.
      "Pattern type": patternTypeLabel(entity.patternType ?? 1),
      // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (DXF group 77) — always shown
      // (AutoCAD greys the row for a non-user-defined pattern, it does not
      // hide it; see the "Double" descriptor in
      // command-engine/descriptors/containers.ts for the full rationale).
      Double: entity.doubleLine ? "Yes" : "No",
      ...(entity.creationScaleName !== undefined
        ? { "Creation scale": entity.creationScaleName }
        : {}),
      __entity_annotative_contexts: annotativeContextsJson,
      __hatch_boundary_paths: boundaryPathsJson,
      ...(gradientJson !== null ? { __hatch_gradient: gradientJson } : {}),
    },
  };
}

export function buildBlockInsertPreview(
  entity: BlockInsertEntity,
  blockRecords: Record<string, BlockRecord>,
  blocks: Record<
    string,
    import("@dxf-ai-editor/drawing-model").BlockDefinition
  >,
  // Drawing-level $INSUNITS (document.insunits). Threaded from the
  // projection caller so the "Block Unit Factor" property reflects the
  // real AutoCAD unit-conversion ratio between the BLOCK_RECORD's
  // insertionUnits and the drawing units. Defaults to 0 (Unitless) so
  // callers with no document context (PDF converters, parity tests)
  // keep AutoCAD's no-conversion behaviour (factor 1).
  drawingInsunits = 0,
): EntityPreview {
  const record = blockRecords[entity.blockName];
  const blockDef = blocks[entity.blockName];
  const dynamicProperties: Record<string, string> = {};
  const dynamicEditable: string[] = [];
  if (blockDef?.dynamicMetadata) {
    // Helper — read a stored customProperties value (string|number|
    // boolean) and return its UI-string rendering. Returns undefined
    // when nothing is stored and the caller should fall back to the
    // parameter's authored current value.
    const readStored = (key: string): string | undefined => {
      const stored = entity.customProperties?.[key];
      if (stored == null) return undefined;
      return typeof stored === "boolean"
        ? stored
          ? "Yes"
          : "No"
        : String(stored);
    };
    for (const param of blockDef.dynamicMetadata.parameters) {
      switch (param.kind) {
        case "linear": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentDistance);
          dynamicEditable.push(key);
          break;
        }
        case "flip": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? (param.currentValue ? "Yes" : "No");
          dynamicEditable.push(key);
          break;
        }
        case "visibility": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ??
            param.states.find((s) => s.id === param.currentStateId)?.name ??
            "";
          dynamicEditable.push(key);
          break;
        }
        case "basepoint": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            `${formatNumber(param.position.x)},${formatNumber(param.position.y)}`;
          // read-only
          break;
        }
        case "point": {
          const keyX = `Param: ${param.name}.x`;
          const keyY = `Param: ${param.name}.y`;
          dynamicProperties[keyX] =
            readStored(`${param.name}.x`) ?? formatNumber(param.currentPoint.x);
          dynamicProperties[keyY] =
            readStored(`${param.name}.y`) ?? formatNumber(param.currentPoint.y);
          dynamicEditable.push(keyX, keyY);
          break;
        }
        case "polar": {
          const keyD = `Param: ${param.name}.distance`;
          const keyA = `Param: ${param.name}.angle`;
          dynamicProperties[keyD] =
            readStored(`${param.name}.distance`) ??
            formatNumber(param.currentDistance);
          dynamicProperties[keyA] =
            readStored(`${param.name}.angle`) ??
            formatNumber(param.currentAngle);
          dynamicEditable.push(keyD, keyA);
          break;
        }
        case "xy": {
          const keyX = `Param: ${param.name}.x`;
          const keyY = `Param: ${param.name}.y`;
          dynamicProperties[keyX] =
            readStored(`${param.name}.x`) ?? formatNumber(param.currentX);
          dynamicProperties[keyY] =
            readStored(`${param.name}.y`) ?? formatNumber(param.currentY);
          dynamicEditable.push(keyX, keyY);
          break;
        }
        case "rotation": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentAngle);
          dynamicEditable.push(key);
          break;
        }
        case "lookup": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? param.currentLookupRowId ?? "";
          dynamicEditable.push(key);
          break;
        }
        // DYN-5 Fix — AutoCAD 2018+ constraint-based parametric blocks
        // (ibeam, швеллер, уголок) expose their dimensional constraints
        // as customProperties keys. Without these cases, DYNSET L1=12000
        // updates entity.customProperties but the preview's properties
        // bag never surfaces it → readInstanceOverrides returns empty →
        // evaluateBlock sees no override → applyConstraintStretch uses
        // authored baseline → no visual stretch.
        case "constraint-horizontal":
        case "constraint-vertical":
        case "constraint-aligned": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentDistance);
          dynamicEditable.push(key);
          break;
        }
        case "constraint-radial": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentRadius);
          dynamicEditable.push(key);
          break;
        }
        case "constraint-diameter": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentDiameter);
          dynamicEditable.push(key);
          break;
        }
        case "constraint-angular": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentAngle);
          dynamicEditable.push(key);
          break;
        }
        case "alignment": {
          const key = `Param: ${param.name}`;
          dynamicProperties[key] =
            readStored(param.name) ?? formatNumber(param.currentAngle ?? 0);
          dynamicEditable.push(key);
          break;
        }
        case "user": {
          const key = `Param: ${param.name}`;
          const stored = readStored(param.name);
          // P7 lookup default: when a user parameter drives the key column
          // of a BlockPropertiesTable (constraint-based parametric block —
          // e.g. "Двутавр" on the ibeam block), AutoCAD shows the first
          // BPT row key as the dropdown's default rather than the raw
          // scalar defaultValue (which is often 0 and meaningless to the
          // user). Match that behavior here so the Properties palette
          // reflects what AutoCAD displays before any selection has been
          // made. String defaults with content (non-BPT user params) and
          // stored customProperties overrides still win.
          let defaultText: string;
          if (
            typeof param.defaultValue === "string" &&
            param.defaultValue.length > 0
          ) {
            defaultText = param.defaultValue;
          } else {
            const bpt = blockDef.dynamicMetadata.blockPropertiesTable;
            const keyDriverHandle = bpt?.keyColumn?.driverParameterHandle;
            const firstRowKey = bpt?.rows?.[0]?.key;
            const isKeyDriver =
              keyDriverHandle !== undefined &&
              keyDriverHandle.length > 0 &&
              (keyDriverHandle === param.id ||
                keyDriverHandle === param.associatedVariableHandle);
            if (isKeyDriver && firstRowKey && firstRowKey.length > 0) {
              defaultText = firstRowKey;
            } else {
              defaultText = formatNumber(Number(param.defaultValue ?? 0));
            }
          }
          dynamicProperties[key] = stored ?? defaultText;
          dynamicEditable.push(key);
          break;
        }
        default: {
          // Exhaustiveness — any unhandled kind is skipped but should
          // surface as a TS error.
          const _never: never = param;
          void _never;
          break;
        }
      }
    }
    // Single visibility state selector (AutoCAD shows one combined
    // "Visibility" dropdown in Properties; we mirror that). When the
    // block has multiple Visibility parameters, AutoCAD uses the first
    // one as the master — we follow suit for Tier 1.
    const firstVis = blockDef.dynamicMetadata.parameters.find(
      (p) => p.kind === "visibility",
    );
    if (firstVis && firstVis.kind === "visibility") {
      // Source order (first non-empty wins):
      //   1. entity.visibilityState  — dedicated field (author/grip edit)
      //   2. entity.customProperties[firstVis.name]  — DYNSET writes here
      //   3. block-def's currentStateId  — authored default
      const current =
        entity.visibilityState ??
        (typeof entity.customProperties?.[firstVis.name] === "string"
          ? (entity.customProperties[firstVis.name] as string)
          : undefined);
      let name = "";
      if (current) {
        const matched =
          firstVis.states.find((s) => s.id === current) ??
          firstVis.states.find((s) => s.name === current);
        name = matched?.name ?? current;
      } else {
        name =
          firstVis.states.find((s) => s.id === firstVis.currentStateId)?.name ??
          "";
      }
      dynamicProperties["Visibility"] = name;
      dynamicEditable.push("Visibility");
    }
  }
  return {
    id: entity.id,
    label: `${entity.blockName} ${entity.id}`,
    type: "Block",
    spaceId: entity.spaceId,
    // local-Codex-review P3 — see buildTextPreview's comment above.
    summary: resolveEntityProvenanceSummary(entity),
    editableProperties: [
      "Layer",
      "Color",
      "Name",
      "Insertion point",
      "Scale",
      "Rotation",
      "Hyperlink",
      "Description",
      "Block Unit",
      "Block Unit Factor",
      "Allow Exploding",
      "Allow Non-Uniform Scale",
      ...dynamicEditable,
    ],
    properties: {
      ...commonProperties(entity),
      Name: entity.blockName,
      "Insertion point": `${formatNumber(entity.insertionPoint.x)},${formatNumber(entity.insertionPoint.y)}`,
      "Insertion X": formatNumber(entity.insertionPoint.x),
      "Insertion Y": formatNumber(entity.insertionPoint.y),
      Scale: `${formatNumber(entity.scaleX ?? 1)},${formatNumber(entity.scaleY ?? 1)},${formatNumber(entity.scaleZ ?? 1)}`,
      "Scale X": formatNumber(entity.scaleX ?? 1),
      "Scale Y": formatNumber(entity.scaleY ?? 1),
      // S15 Slice G — emit EVERY typed-writer-OWNED INSERT transform scalar as its
      // own bag key so the owned set ⊆ the import bag (the Slice-A plinegen /
      // Slice-F View-twist class): a registry-owned label absent from the bag has no
      // Properties-grid row + no `properties/apply` undo-inverse source. "Scale Z"
      // joins the sibling "Scale X"/"Scale Y" (only the combined "Scale" carried it
      // before); the MINSERT grid fields (Column/Row count + spacing) were emitted by
      // NEITHER the combined nor an individual key. All match the descriptor display
      // (`formatNumber`, group 41/42/43/50/70/71/44/45) so a re-dispatch round-trips.
      "Scale Z": formatNumber(entity.scaleZ ?? 1),
      Rotation: formatNumber(entity.rotation ?? 0),
      "Column count": formatNumber(entity.columnCount ?? 1),
      "Row count": formatNumber(entity.rowCount ?? 1),
      "Column spacing": formatNumber(entity.columnSpacing ?? 0),
      "Row spacing": formatNumber(entity.rowSpacing ?? 0),
      Hyperlink: "",
      Description: record?.description ?? "",
      "Block Unit": String(record?.insertionUnits ?? 0),
      // AutoCAD INSERT auto-scales a block by the unit-conversion ratio
      // between the BLOCK_RECORD's insertionUnits (group 70) and the
      // drawing's $INSUNITS. computeBlockUnitFactor returns 1 when either
      // side is 0/Unitless or unknown — preserve-source, no inference.
      "Block Unit Factor": formatUnitFactor(
        computeBlockUnitFactor(drawingInsunits, record?.insertionUnits ?? 0),
      ),
      "Allow Exploding": record?.explodable === false ? "No" : "Yes",
      "Allow Non-Uniform Scale": record?.scalable === "uniform" ? "No" : "Yes",
      ...(entity.creationScaleName !== undefined
        ? { "Creation scale": entity.creationScaleName }
        : {}),
      // PR-3 — annotative INSERT contexts (blockref variant). Renderer
      // reads via useCannoscaleResolver and overrides insertion / scaleX /
      // scaleY / rotation per active CANNOSCALE. Empty for non-annotative
      // INSERTs.
      __entity_annotative_contexts: JSON.stringify(
        entity.annotativeContexts ?? [],
      ),
      ...dynamicProperties,
    },
  };
}

function buildArrayRectPreview(entity: ArrayRectEntity): EntityPreview {
  return {
    id: entity.id,
    label: `Array ${entity.id}`,
    type: "Rectangular Associative Array",
    spaceId: entity.spaceId,
    summary: t("audit.imported_array"),
    editableProperties: [
      "Layer",
      "Color",
      "Rows",
      "Columns",
      "Row spacing",
      "Column spacing",
      "Axis angle",
      "Associative",
    ],
    properties: {
      ...commonProperties(entity),
      Rows: String(entity.rowCount ?? 3),
      Columns: String(entity.columnCount ?? 3),
      "Row spacing": formatNumber(entity.rowSpacing ?? 50),
      "Column spacing": formatNumber(entity.columnSpacing ?? 50),
      // S-ARRAYRECT-AXISANGLE-UNIT (Codex P2, local review rounds 4 + 9) —
      // AutoCAD's Properties palette always shows Axis angle in DEGREES,
      // with 0deg meaning "not rotated from standard" (every other rotation
      // field's zero convention). The typed `axisAngle` STORAGE is neither
      // that unit NOR that baseline directly for an associative array: it is
      // the resolved AcDbAssocArrayRectangularParameters "AxesAngle" —
      // RADIANS (byte-matched — packages/dxf-io/src/handleResolution.ts
      // RECT_NAMED_VALUE_PARAMS), the angle BETWEEN the row/column axes, NOT
      // the array's absolute rotation (ODA CalculateDefaultMatrix: Y-axis =
      // X-axis-direction.rotateBy(AxesAngle)). A STANDARD orthogonal array
      // (rows perpendicular to columns — the overwhelming common case)
      // therefore persists AxesAngle = pi/2 (90deg), confirmed byte-identical
      // against two real AutoCAD fixtures
      // (packages/drawing-model/src/dxfBridge.arrayAssoc.test.ts: "AxesAngle
      // in radians (pi/2 = the 90deg default)") — round 4 solved the
      // radians-vs-degrees unit, round 9 additionally subtracts the 90deg
      // neutral baseline so a STANDARD array displays/renders at 0deg, not
      // 90deg (the array's TRUE absolute orientation, "xAxisDirection", is a
      // separate still-undecoded field per assocArrayParser.ts's "Researched
      // deferrals" — this is the best-available approximation given that
      // gap: correct for the standard, non-sheared, non-rotated array every
      // currently-decodable real fixture represents). A non-associative
      // (bare INSERT-grid) array's angle comes straight off DXF group 50,
      // which is DEGREES with the ordinary 0deg-neutral convention already —
      // unaffected by either correction. Project the user-facing DEGREES
      // value HERE, ONCE — every consumer of this bag string (the
      // render/grip helpers via apps/web renderHelpers.ts
      // resolveArrayAxisAngleDeg, the Properties-panel bag-residual path)
      // then reads an ALREADY-CORRECT degree value with no further unit or
      // baseline correction. Mirrored 1:1 by the typed `arrayRectDescriptors`
      // "Axis angle" descriptor's `display`
      // (packages/command-engine/src/descriptors/containers.ts
      // `resolveArrayAxisAngleDisplayDeg`, the SAME formula) — the S16
      // panelReadCutover census gate keeps the two byte-identical.
      "Axis angle": formatNumber(
        entity.associative
          ? ((entity.axisAngle ?? 0) * 180) / Math.PI - 90
          : (entity.axisAngle ?? 0),
      ),
      "Base X": formatNumber(entity.basePoint?.x ?? 0),
      "Base Y": formatNumber(entity.basePoint?.y ?? 0),
      // S-ARRAYRECT-BASEPOINT-Z — the array's base elevation (group 30 on
      // the ARRAYRECT block-ref, already round-tripped through dxf-io).
      // Consumed by the W1.5 render arm so every instance (including a
      // 3-axis array's level 0) sits AT the real base elevation instead of
      // being implicitly reset to world Z=0.
      "Base Z": formatNumber(entity.basePoint?.z ?? 0),
      Associative: entity.associative ? "Yes" : "No",
      // PR-D — encode source entity ids so the renderer can resolve
      // `baseEntityIds` for the InstancedMesh transform pass.
      __array_source_ids: JSON.stringify(entity.sourceEntityIds ?? []),
      // W1.5 — internal renderer-only channel (mirrors `__array_source_ids`
      // above): projects the typed §9.4 3-axis Z-level fields
      // (`ArrayRectEntity.levelCount`/`levelSpacing`, already round-tripped
      // through dxf-io) so `arrayRectToInstanced` can stack the grid along
      // Z. NOT in `editableProperties` — full Level count/spacing EDITING
      // stays deferred to the AW-3 write-back arm (Codex #502 "Levels
      // controls cannot be exposed before render — render lands first").
      // Absent levelCount defaults to a single level (2D array unchanged).
      __array_level_count: String(entity.levelCount ?? 1),
      __array_level_spacing: formatNumber(entity.levelSpacing ?? 0),
      // S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review round 6) —
      // internal renderer-only channel (mirrors `__array_level_count` above):
      // projects the typed `ArrayRectEntity.basePlaneNormal` (already
      // round-tripped through dxf-io — `dxfBridge.arrayAssocImport.ts`) so
      // the W1.5 Z-level render arm can stack a TILTED 3-axis array's levels
      // along the array's own base-plane normal instead of assuming WCS +Z.
      // Absent for the overwhelmingly common flat (default (0,0,1) normal)
      // array — `computeOcsBasis`'s identity fast-path keeps that case
      // byte-identical to the pre-existing WCS-Z-only render. NOT in
      // `editableProperties` — normal editing is not part of this render-arm
      // (matches the `Base Z` / Level count/spacing deferred-editing class).
      __array_base_plane_normal: entity.basePlaneNormal
        ? JSON.stringify(entity.basePlaneNormal)
        : "",
    },
  };
}

function buildViewportPreview(entity: ViewportEntity): EntityPreview {
  // Calculate scale from viewport height / view height
  const vpScale =
    entity.viewHeight && entity.viewHeight > 0
      ? entity.height / entity.viewHeight
      : 0.01;
  const scaleLabel = formatViewportScale(vpScale);

  return {
    id: entity.id,
    label: `Viewport ${entity.id}`,
    type: "Viewport",
    spaceId: entity.spaceId,
    summary: t("audit.imported_viewport"),
    // "Shade plot" is intentionally NOT in editableProperties: it is
    // DISPLAY-ONLY in the PROPERTIES grid (the value projection below STAYS,
    // informational). It is edited via the dedicated ViewportInspector dropdown
    // (command path: applyViewportSetShadePlot, full guards + faithful undo);
    // MATCHPROP/Properties-apply copy is DEFERRED — viewport-special
    // MATCHPROP-persistence is a family-wide subsystem (none of the six
    // viewport-special fields persist via copy).
    editableProperties: [
      "Layer",
      "Color",
      "Linetype",
      "Lineweight",
      "Display lock",
      "Scale",
      "Center X",
      "Center Y",
      "Width",
      "Height",
      "View height",
      "View center X",
      "View center Y",
      // @codex round-5 P2-2 — View twist (group 51) is a registry-derived OWNED
      // plain leaf (geometryReducer Step 1d writes viewTwistAngle); advertise it
      // editable so the Properties-grid row is offered + undo-lockstep-covered (the
      // "View twist" bag key is already emitted in `properties` below). Was omitted
      // → the one advertised-editable gap of the four plain leaves.
      "View twist",
    ],
    properties: {
      ...commonProperties(entity),
      "Display lock": entity.displayLocked ? "Yes" : "No",
      Scale: scaleLabel,
      // Shade plot (DXF group 170) projected from the REAL stored mode — never
      // hardcoded (a hardcoded "As displayed" was a PRIORITY-#1 fidelity lie that
      // contradicted the ViewportInspector dropdown for Hidden/Rendered VPs).
      // Canonical English label; the dropdown renders the localized label
      // independently — both share the SHADE_PLOT_MODES identity mapping in
      // viewportShadePlot.ts. Imported 4/5 surface verbatim as "Other (N)".
      "Shade plot": shadePlotModeEnglishLabel(entity.shadePlotMode ?? 0),
      "Center X": formatNumber(entity.center.x),
      "Center Y": formatNumber(entity.center.y),
      Width: formatNumber(entity.width),
      Height: formatNumber(entity.height),
      "View height": formatNumber(entity.viewHeight),
      "View center X": formatNumber(entity.viewCenter?.x ?? 0),
      "View center Y": formatNumber(entity.viewCenter?.y ?? 0),
      "View twist": formatNumber(entity.viewTwistAngle ?? 0),
      "Viewport status": String(entity.viewportStatus ?? 1),
      "Frozen layers": (entity.frozenLayers ?? []).join(", "),
      // PR-13 — per-viewport annotation scale. Surfaced as preview
      // properties so renderViewportContent can pick them up without a
      // separate channel. Empty string when the field is absent — the
      // renderer falls back to the document-level `cannoscaleName` /
      // `annoAllVisible` when these are unset (legacy viewports without
      // a per-viewport CANNOSCALE round-trip identically).
      "Viewport cannoscale name": entity.viewportCannoscaleName ?? "",
      "Viewport cannoscale factor":
        entity.viewportCannoscaleFactor !== undefined
          ? String(entity.viewportCannoscaleFactor)
          : "",
      "Viewport anno all visible":
        entity.viewportAnnoAllVisible !== undefined
          ? entity.viewportAnnoAllVisible
            ? "true"
            : "false"
          : "",
      // EPIC-VIEWPORT-CLIP SLICE 3 — project the typed resolved clip polygon +
      // membership index onto the two property-bag keys the 3D renderer adapter
      // reads (viewportToRenderable.ts → viewportClipping stencil pass). The
      // typed model fields (`clipPath` / `childEntityIds`) are populated by the
      // OBJECTS second pass (dxfBridge.import resolveViewportClip /
      // buildViewportMembershipIndex). Only emit a key when the field is present
      // so an unclipped viewport (no SPATIAL_FILTER) keeps the rectangular-window
      // fallback in the adapter (clipPath omitted ⇒ rect clip).
      ...(entity.clipPath && entity.clipPath.length >= 2
        ? { __viewport_clip_path: JSON.stringify(entity.clipPath) }
        : {}),
      ...(entity.childEntityIds && entity.childEntityIds.length > 0
        ? { __viewport_child_ids: JSON.stringify(entity.childEntityIds) }
        : {}),
    },
  };
}

/** Format viewport scale as human-readable ratio (e.g. "1:100", "2:1"). */
function formatViewportScale(scale: number): string {
  if (scale <= 0) return "1:100";
  if (scale >= 1) {
    const ratio = Math.round(scale * 100) / 100;
    if (Math.abs(ratio - Math.round(ratio)) < 0.01) {
      return `${Math.round(ratio)}:1`;
    }
    return `${ratio.toFixed(2)}:1`;
  }
  const inv = 1 / scale;
  const rounded = Math.round(inv);
  if (Math.abs(inv - rounded) < 0.5) {
    return `1:${rounded}`;
  }
  return `1:${Math.round(inv * 10) / 10}`;
}

function normalizeAngleSpan(startAngle: number, endAngle: number): number {
  const span = (((endAngle - startAngle) % 360) + 360) % 360;
  return span === 0 && startAngle !== endAngle ? 360 : span;
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }

  return Number(value.toFixed(3)).toString();
}

/**
 * Codex #321 P2 — Block Unit Factor must NOT round tiny-but-nonzero
 * factors to "0".
 *
 * The Block Unit Factor is the INSUNITS conversion ratio
 * (`drawingUnitsInMeters / blockUnitsInMeters`) AutoCAD applies on INSERT.
 * Legitimate extreme unit combos make this ratio span many orders of
 * magnitude — e.g. a mm block (4) in a km drawing (7) gives 1e-6, and a
 * mm block in a Gigameter drawing (17) gives 1e-12. `formatNumber` rounds
 * to 3 decimals, so any |factor| < 0.0005 collapses to "0", which is
 * indistinguishable from the genuine factor-1 fallback (unitless / unknown
 * units) and visually wrong.
 *
 * This formatter keeps the common, human-readable factors exactly as
 * `formatNumber` would (1, 0.001, 25.4, 304.8, integers like 1e6), but
 * switches to scientific notation when the magnitude falls outside the
 * 3-decimal display window — so a faithful nonzero value is always shown.
 */
function formatUnitFactor(value: number): string {
  if (value === 0 || !Number.isFinite(value)) {
    return formatNumber(value);
  }
  const magnitude = Math.abs(value);
  // Below the 3-decimal floor (0.0005 rounds to 0.001 → smallest non-zero
  // formatNumber output) or above the precision-safe integer window
  // (1e15, where formatNumber's String() starts emitting exponent forms /
  // loses digits) — fall back to compact scientific notation so the value
  // never reads as "0" and never distorts. `toExponential()` with no
  // argument keeps only the significant digits needed.
  if (magnitude < 0.0005 || magnitude >= 1e15) {
    return value.toExponential();
  }
  return formatNumber(value);
}

/**
 * Codex P2-A — FULL-PRECISION geometry property values for the undo
 * inverse.
 *
 * The `properties/apply` inverse (editorStore) snapshots the pre-mutation
 * EntityPreview property strings — but those are produced by
 * `formatNumber`, which ROUNDS to 3 decimals. Replaying that inverse on
 * undo therefore restored a 3-decimal-rounded coordinate, NOT the exact
 * pre-drag document value (e.g. undo of a LINE end gave 7.988 instead of
 * 7.987654321). This helper re-derives the SAME precision-sensitive
 * geometry property keys the preview builders emit, but from the RAW
 * document entity values via `String(...)` (no rounding), so the inverse
 * restores exact coordinates.
 *
 * Returns ONLY the geometry/coordinate keys (the ones a grip drag or
 * Properties-panel geometry edit can change). Common fields
 * (Layer/Color/…) are NOT precision-sensitive and keep coming from the
 * formatted preview snapshot. Keys here MUST match the preview builders'
 * key spelling exactly (e.g. `buildLinePreview` "Start X"/"End X").
 */
export function rawGeometryPropertyValues(
  entity: DrawingEntity,
): Record<string, string> {
  const raw = (n: number): string => String(n);
  switch (entity.type) {
    case "Line":
      return {
        "Start X": raw(entity.start.x),
        "Start Y": raw(entity.start.y),
        // Codex residual R2 (#317) — LINE start/end now expose an editable Z
        // (group 30/31). Snapshot the RAW (unrounded) endpoint Z so an undo of a
        // grip / Properties edit restores the EXACT elevation; without it the
        // inverse fell back to the formatNumber-rounded preview `Start Z`/`End Z`
        // (3 decimals). `z ?? 0` matches the model + export contract.
        "Start Z": raw(entity.start.z ?? 0),
        "End X": raw(entity.end.x),
        "End Y": raw(entity.end.y),
        "End Z": raw(entity.end.z ?? 0),
      };
    case "Point":
      return {
        "Position X": raw(entity.location.x),
        "Position Y": raw(entity.location.y),
        // S15 Slice B — Point now exposes an editable group-30 Z + Rotation
        // (typed-writer-owned, geometryReducer Step 1d). Snapshot the RAW
        // (unrounded) values so an undo of a Properties edit restores the
        // EXACT elevation / rotation; without these keys the inverse fell
        // back to the formatNumber-rounded preview strings (3 decimals).
        // `z ?? 0` matches the model + export contract (mirror of Circle/Arc
        // "Center Z"). Rotation is DXF group 50 = DEGREES, stored + displayed
        // verbatim (identity descriptor); snapshot the raw (unrounded) degrees
        // so undo restores the EXACT stored value.
        "Position Z": raw(entity.location.z ?? 0),
        Rotation: raw(entity.rotation),
      };
    case "Circle":
      return {
        "Center X": raw(entity.center.x),
        "Center Y": raw(entity.center.y),
        // S-ARC-CIRCLE-CENTER-Z — CIRCLE center now exposes an editable group-30
        // Z (mirror of LINE Start/End Z). Snapshot the RAW (unrounded) center Z
        // so an undo of a grip / Properties edit restores the EXACT elevation;
        // without it the inverse fell back to the formatNumber-rounded preview
        // `Center Z`. `z ?? 0` matches the model + export contract.
        "Center Z": raw(entity.center.z ?? 0),
        Radius: raw(entity.radius),
      };
    case "Arc":
      return {
        "Center X": raw(entity.center.x),
        "Center Y": raw(entity.center.y),
        // S-ARC-CIRCLE-CENTER-Z — ARC center group-30 Z (see CIRCLE above).
        "Center Z": raw(entity.center.z ?? 0),
        Radius: raw(entity.radius),
        "Start angle": raw(entity.startAngle),
        "End angle": raw(entity.endAngle),
      };
    case "Ellipse":
      // S15 Slice B — Ellipse geometry is now typed-writer-persisted
      // (geometryReducer Step 1d): center (10/20), major-axis endpoint VECTOR
      // relative to center (11/21), ratio (40), start/end parameters in
      // RADIANS (41/42). Snapshot the RAW (unrounded) values so the undo
      // inverse restores the EXACT stored geometry instead of the
      // formatNumber-rounded preview strings (same #317 P2-3 class as the
      // Circle/Arc/LWPOLYLINE raw overlays above). Params are stored AND
      // displayed as radians (`buildEllipsePreview`), so no unit conversion.
      return {
        "Center X": raw(entity.center.x),
        "Center Y": raw(entity.center.y),
        "Major axis X": raw(entity.majorAxisEndpoint.x),
        "Major axis Y": raw(entity.majorAxisEndpoint.y),
        Ratio: raw(entity.ratio),
        "Start parameter": raw(entity.startParam),
        "End parameter": raw(entity.endParam),
      };
    case "LWPOLYLINE":
      return {
        Vertices: entity.vertices
          .map((v) => `${raw(v.x)},${raw(v.y)}`)
          .join(";"),
        // Codex P2-3 (#317) — `Bulges` is now a patchable + persisted property,
        // so the undo inverse MUST carry the EXACT pre-edit per-vertex bulges.
        // Without this key the inverse falls back to the formatted preview
        // `Bulges` string (formatNumber → 3 decimals), so undo of a bulge edit
        // restored ROUNDED arc data (and exported a different DXF). Emit raw
        // (unrounded) bulges, mirroring the raw `Vertices` snapshot above.
        Bulges: entity.vertices.map((v) => raw(v.bulge ?? 0)).join(";"),
        // S15 Slice A review round — Elevation (group 38) + Constant width
        // (group 43) are now typed-writer-persisted numeric labels, so their
        // undo inverse must carry the RAW (unrounded) document values too —
        // the same #317 P2-3 class as Bulges above: without these keys the
        // inverse restored the formatNumber-rounded preview strings.
        Elevation: raw(entity.elevation ?? 0),
        "Constant width": raw(entity.constantWidth ?? 0),
      };
    case "Spline": {
      // Codex P2-6 (#317) — emit 3-D point strings (x,y,z) so an undo of a
      // fit-grip drag restores each control / fit point's EXACT Z even when
      // the control-point COUNT changed (a recompute changes count, breaking
      // the index-by-index Z fallback the 2-D form relied on). Also snapshot
      // the knot vector so undo restores a custom / non-uniform knot vector
      // that the forward fit recompute overwrote with a clamped-uniform one.
      const out: Record<string, string> = {};
      if (entity.controlPoints.length > 0) {
        out["Control points"] = entity.controlPoints
          .map((p) => `${raw(p.x)},${raw(p.y)},${raw(p.z ?? 0)}`)
          .join(";");
      }
      if (entity.fitPoints.length > 0) {
        out["Fit points"] = entity.fitPoints
          .map((p) => `${raw(p.x)},${raw(p.y)},${raw(p.z ?? 0)}`)
          .join(";");
      }
      if (entity.knots.length > 0) {
        out["Knots"] = entity.knots.map((k) => raw(k)).join(",");
      }
      // Codex P2 (#317·round-7) — snapshot the RATIONAL flag + per-control-point
      // weights so an undo of a fit-grip recompute (which clears weights=[] +
      // rational=false for the resulting non-rational fit spline) RESTORES the
      // original rational NURBS exactly. Without these keys the inverse carried
      // only the formatNumber-rounded preview `Weights`/`Rational` (and the
      // Spline doc translator skipped them entirely), so undo left the spline
      // non-rational with empty weights = data loss of the original weights.
      // Emit always (a non-rational spline has weights=[] + Rational "No", which
      // the inverse `next` carries so redo re-clears symmetrically).
      out["Rational"] = entity.rational ? "Yes" : "No";
      out["Weights"] = entity.weights.map((w) => raw(w)).join(",");
      return out;
    }
    case "Text":
      // GRIPS-DRAG-MORE-TYPES — TEXT "Insertion X"/"Insertion Y" is
      // typed-writer-persisted (geometryReducer Step 1d,
      // `TYPED_EXTENDED_GEOMETRY_KINDS` includes "Text"; `descriptors/text.ts`
      // registers `coord("Insertion X", "insertionPoint.x", …)`), so its undo
      // inverse must carry the RAW (unrounded) document `insertionPoint` — the
      // same #317 P2-3 class as the Block/Circle/Ellipse raw overlays above.
      // There was NO "Text" case here before (fell through to `default: {}`),
      // so an undo of an insertion grip move / "Insertion X/Y" Properties edit
      // restored the formatNumber-rounded preview strings (3 decimals) instead
      // of the exact pre-edit coordinates. `insertionPoint` has no `z` (TEXT's
      // is a plain 2-D `drawingPointSchema`, unlike Line/Circle/Block), so
      // there is no elevation key to snapshot alongside X/Y.
      //
      // NOTE — "Alignment X"/"Alignment Y" is NOT snapshotted here even though
      // the insertion move shifts it too (GRIPS-DRAG-MORE-TYPES round 2,
      // `textInsertionMoveMutator`): this overlay exists to raw-precision-
      // correct the undo restore of labels a `properties/apply` DIRECTLY
      // SUBMITS, and "Alignment X"/"Alignment Y" are never themselves
      // submitted (they stay excluded from the typed-writer owned set — see
      // `EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS`'s doc in
      // typedPropertyWriter.ts). The DOCUMENT-level undo (which the grip-drag
      // round-trip contract exercises) restores `alignmentPoint` correctly on
      // its own — the mutator's coupled write lands in the SAME top-level-
      // field diff the typed writer produces for `insertionPoint`, which the
      // document reducer's inverse mechanism captures regardless of this
      // shell-only overlay.
      return {
        "Insertion X": raw(entity.insertionPoint.x),
        "Insertion Y": raw(entity.insertionPoint.y),
      };
    case "MText":
      // Codex P2 (#417) — `rawGeometryPropertyValues` had NO MText case, so it
      // fell through to `{}` and an undo of a Properties edit of an IMPORTED MText
      // (e.g. a Color edit alongside the full-draft re-dispatch) restored the
      // 3-decimal `formatNumber`-rounded preview Height/Width/Rotation/Line
      // spacing instead of the RAW DXF precision (`nominalHeight: 2.123456` →
      // 2.123). #417 made these grid rows map to REAL numeric fields
      // (nominalHeight group 40, referenceWidth group 41, rotation group 50,
      // lineSpacingFactor group 44), so they are now replayable through the
      // translator on undo — the inverse MUST carry their unrounded values.
      // Mirrors the Line raw Start/End Z overlay. Keys match the preview builder
      // spelling exactly (`deriveMtextPreviewProperties`: Height/Width/Rotation/
      // "Line spacing").
      //
      // GRIPS-DRAG-MORE-TYPES — "Insertion X"/"Insertion Y" is ALSO
      // typed-writer-persisted (the same `TYPED_EXTENDED_GEOMETRY_KINDS`
      // "MText" set as Height/Width/Rotation above) but was missing from this
      // overlay — an undo of an insertion grip move restored the rounded
      // preview coordinate. Mirrors the new Text case above.
      return {
        Height: raw(entity.nominalHeight),
        Width: raw(entity.referenceWidth),
        Rotation: raw(entity.rotation),
        "Line spacing": raw(entity.lineSpacingFactor),
        "Insertion X": raw(entity.insertionPoint.x),
        "Insertion Y": raw(entity.insertionPoint.y),
      };
    case "Block":
      // Codex residual R3 (#317) — `rawGeometryPropertyValues` had NO Block case,
      // so it fell through to `{}` (truthy → the inverse overlay spread nothing)
      // and the undo of an insertion grip move / "Insertion X/Y" Properties edit
      // restored the formatNumber-rounded preview `Insertion X/Y` (3 decimals).
      // Snapshot the RAW (unrounded) insertion X/Y so the inverse restores the
      // EXACT pre-edit coordinates, mirroring the Line/Circle raw snapshots.
      //
      // S15 Slice G — the INSERT transform scalars (Scale X/Y/Z group 41/42/43,
      // Rotation group 50, MINSERT Column/Row count group 70/71, Column/Row spacing
      // group 44/45) are now typed-writer-persisted (geometryReducer Step 1d, via
      // `blockDescriptors`), so their undo inverse MUST carry the RAW (unrounded)
      // document values too — the same #317 P2-3 class as Insertion X/Y above and
      // the full Viewport overlay (Slice F). Without these keys a palette undo of a
      // Scale / Rotation / spacing edit on an imported / high-precision block
      // restored the 3-decimal formatNumber preview string. Counts are integers
      // (formatNumber-lossless) but are carried for a clean "every typed-writer-owned
      // numeric leaf snapshots its raw undo value" invariant. The 2-D Insertion edit
      // never touches group-30 Z, so the forward writer re-reads Z from the live
      // document on undo (XY_PRESERVE zPreserve) — X/Y here is sufficient.
      return {
        "Insertion X": raw(entity.insertionPoint.x),
        "Insertion Y": raw(entity.insertionPoint.y),
        "Scale X": raw(entity.scaleX),
        "Scale Y": raw(entity.scaleY),
        "Scale Z": raw(entity.scaleZ),
        Rotation: raw(entity.rotation),
        "Column count": raw(entity.columnCount),
        "Row count": raw(entity.rowCount),
        "Column spacing": raw(entity.columnSpacing),
        "Row spacing": raw(entity.rowSpacing),
      };
    case "Rectangular Associative Array":
      // Codex residual R3 (#317) — same missing-case bug for the array base
      // point: no case → `{}` → undo restored the rounded preview `Base X/Y`.
      // Snapshot the RAW base X/Y (the translator uses the `Base X` / `Base Y`
      // labels) so undo of an origin grip move / Base edit restores the exact
      // pre-edit coordinates. Base-point Z is re-read from the document on undo.
      return {
        "Base X": raw(entity.basePoint?.x ?? 0),
        "Base Y": raw(entity.basePoint?.y ?? 0),
      };
    case "Viewport":
      // @codex round-4 P2-2 (S15 Slice F) — the Viewport numeric rows are now
      // typed-writer-persisted (Step 1d plain leaves + Step 1d-se side-effect), so
      // their undo inverse MUST carry the RAW (unrounded) document values — the same
      // #317 class as the LWPOLYLINE / Point / Ellipse overlays above. Without this
      // case the inverse fell back to the formatNumber-rounded (3-decimal) preview
      // strings, so undoing a viewport Properties edit on an imported / high-
      // precision value restored ROUNDED geometry/view data. Center X/Y = group
      // 10/20 (paper-space center), Width = 40, Height = 41, View center X/Y = 12/22,
      // View height = 45, View twist = 51 (degrees verbatim). (The Scale + Display
      // lock side-effect carriers — vpOverrideScale / VP_OVERRIDES XDATA / lock — are
      // restored VERBATIM by the single-viewport composed snapshot-restore inverse in
      // editorStore; these raw overlays cover the plain-numeric precision for the
      // MULTI-entity apply that keeps the label-replay inverse.)
      return {
        "Center X": raw(entity.center.x),
        "Center Y": raw(entity.center.y),
        Width: raw(entity.width),
        Height: raw(entity.height),
        "View height": raw(entity.viewHeight),
        "View center X": raw(entity.viewCenter?.x ?? 0),
        "View center Y": raw(entity.viewCenter?.y ?? 0),
        "View twist": raw(entity.viewTwistAngle ?? 0),
      };
    case "Revision Cloud":
      // REVCLOUD-PROPS-EDITOR (§5.1) — the two arc-length fields now persist via
      // the document translator (labelToFieldByType["Revision Cloud"]), so their
      // `properties/apply` undo inverse MUST carry the RAW document values, the
      // same #317-class overlay as the geometry rows above. Two reasons this case
      // is load-bearing (@codex #487 P1/P2 on the integration head):
      //   • "Minimum arc length" (arcChordLength) is NOT emitted by the revcloud
      //     PREVIEW builder, so `buildPropertiesApplyInverseFromSnapshots` (which
      //     diffs prev/next = preview bag ∪ THIS raw overlay) would see NO changed
      //     key for a minimum-arc-only edit → no undo entry at all (P1). The raw
      //     overlay is spread onto both prev/next, so this key makes it diffable.
      //   • "Approximate arc length" IS in the preview bag but only at formatNumber
      //     (3-decimal) precision; an imported cloud's full-precision
      //     `approximateArcLength` (e.g. 12.345678) would undo to 12.346 — the raw
      //     overlay restores the exact DXF precision (P2). Labels match the form /
      //     translator spelling exactly.
      return {
        "Minimum arc length": raw(entity.arcChordLength),
        "Approximate arc length": raw(entity.approximateArcLength),
      };
    case "Polyline":
      // @codex F1 (P1) — the legacy heavy POLYLINE's writable numeric rows are
      // typed-writer-persisted (geometryReducer Step 1d), so their undo inverse
      // must carry the RAW (unrounded) document values — the same #317 class as
      // the LWPOLYLINE / Point / Viewport overlays above. Without this case the
      // inverse fell back to the formatNumber-rounded (3-decimal) preview
      // strings, so undoing an edit on an imported high-precision default width
      // or elevation restored a rounded value.
      //
      // `?? 0` on the two widths mirrors the preview builder's own projection
      // (`buildPolylinePreview`: `formatNumber(entity.defaultStartWidth ?? 0)`)
      // and the descriptor display: groups 40/41 are OPTIONAL (absent ⇒ every
      // vertex inherits 0), and both factories show the inherited 0 — which is
      // what AutoCAD shows too.
      //
      // @codex PR #785 round 2, F2 — that `?? 0` is a DISPLAY projection, and a
      // display string cannot distinguish an ABSENT group 40/41 from one written
      // with the value its absence displays. Round 1 documented the consequence
      // (undoing an edit that INTRODUCED a width onto a polyline with no group
      // 40 restored an EXPLICIT zero, so the entity did not return to its
      // pre-edit state and the next export emitted a group the file never had)
      // as an accepted limitation. It is not one — history restores the
      // pre-state exactly — and it is now FIXED, not by teaching this overlay a
      // per-field absence spelling, but by `editorStore`'s presence sweep:
      // when an apply makes ANY previously-absent field present, the pre-edit
      // TYPED SNAPSHOT is composed into the inverse and restores the entity
      // verbatim (see `propertiesApplyPresenceRestore.ts`). So this overlay stays
      // purely about PRECISION, exactly like every other case here, and the
      // presence bits are carried by the mechanism that cannot miss one.
      //
      // The group-70 bit rows (Closed / Linetype generation) are exact
      // "true"/"false" strings in the bag, so they need no raw overlay.
      return {
        "Start segment width": raw(entity.defaultStartWidth ?? 0),
        "End segment width": raw(entity.defaultEndWidth ?? 0),
        Elevation: raw(entity.elevation.z ?? 0),
      };
    case "Table":
      // @codex F1 (P1) — the ACAD_TABLE insertion point is typed-writer-persisted
      // (Step 1d), so its undo must restore the EXACT pre-edit coordinates rather
      // than the 3-decimal preview strings. Keys match `buildTablePreview`.
      return {
        "Position X": raw(entity.insertionPoint.x),
        "Position Y": raw(entity.insertionPoint.y),
        "Position Z": raw(entity.insertionPoint.z ?? 0),
      };
    case "OLE Frame":
      // @codex F1 (P1) — same for the OLE2FRAME corners (groups 10/20 + 11/21),
      // which ARE the entity's geometry. Keys match `buildOleFramePreview`.
      return {
        "Upper left X": raw(entity.upperLeft.x),
        "Upper left Y": raw(entity.upperLeft.y),
        "Lower right X": raw(entity.lowerRight.x),
        "Lower right Y": raw(entity.lowerRight.y),
      };
    default:
      return {};
  }
}
